/**
 * โมเดล low-poly ของตัวละครและมอนสเตอร์
 *
 * ย้ายมาจาก `demo/wickhaven.html` (ฟังก์ชัน `chibi`, `buildMon`, `animChibi`)
 * แล้วเปลี่ยนจาก global `BABYLON` เป็น import แยกโมดูลให้ตรงกับ client ตัวจริง
 *
 * เป็นงานฝั่งภาพล้วน ไม่รู้เรื่องกฎเกม ไม่อ่าน state และไม่ตัดสินอะไร
 * รูปร่างทั้งหมดต่อจากทรงพื้นฐานของ Babylon ยังไม่ใช่โมเดลจาก Gemini
 */

import type { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3, Vector3 } from "@babylonjs/core/Maths/math";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { animateRig, type CharacterRig } from "./rig.js";
import { buildRaceModel } from "./race-model.js";
import {
  DEFAULT_GENDER,
  MONSTERS,
  type CharacterLook,
  type ClassLook,
  type EquipLook,
  type GearAnchor,
  type Gender,
  type PetLook,
} from "@wickhaven/shared";

interface MatOpts {
  /** สีเรืองแสง ใช้กับของที่ควรสว่างในที่ร่ม */
  em?: string;
  alpha?: number;
  /** ไม่รับแสงเลย ใช้กับไฮไลต์ตาและปีกใส */
  noLight?: boolean;
}

/**
 * วัสดุสีเดียวแบบใช้ซ้ำ
 *
 * แคชตามสีกับ option เพราะโมเดลหนึ่งตัวใช้ซ้ำหลายชิ้น และผู้เล่นหลายคนใช้สีเดียวกัน
 * ถ้าสร้างใหม่ทุกชิ้นจะได้ StandardMaterial เป็นร้อยตัวซึ่งแต่ละตัวคอมไพล์ shader เอง
 */
/**
 * **แคชแยกตามฉาก** ห้ามใช้ Map เดียวทั้งโปรเจกต์
 *
 * วัสดุผูกกับฉากและ WebGL context ที่สร้างมัน ถ้าเอาวัสดุของฉากหลัก
 * ไปใช้กับฉากอื่น (เช่นรูปหน้าตัวละครที่มี Engine ของตัวเอง) จะไม่มีอะไรวาดออกมาเลย
 * โดยไม่มี error ให้เห็น — เคยทำให้กรอบรูปหน้าว่างเปล่ามาแล้ว
 */
const matCache = new WeakMap<Scene, Map<string, StandardMaterial>>();

function mat(scene: Scene, hex: string, opts: MatOpts = {}): StandardMaterial {
  let perScene = matCache.get(scene);
  if (!perScene) {
    perScene = new Map();
    matCache.set(scene, perScene);
  }

  const key = hex + JSON.stringify(opts);
  const found = perScene.get(key);
  if (found) return found;

  const m = new StandardMaterial(`m${key}`, scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.specularColor = Color3.Black();
  if (opts.em) m.emissiveColor = Color3.FromHexString(opts.em);
  if (opts.alpha != null) m.alpha = opts.alpha;
  if (opts.noLight) m.disableLighting = true;
  m.freeze();
  perScene.set(key, m);
  return m;
}

const faceMatCache = new WeakMap<Scene, Map<string, StandardMaterial>>();

function lighten(hex: string, amt: number): string {
  const n = parseInt(hex.replace("#", ""), 16);
  if (isNaN(n)) return "#60a5fa";
  const r = Math.min(255, Math.round(((n >> 16) & 255) + (255 - ((n >> 16) & 255)) * amt));
  const g = Math.min(255, Math.round(((n >> 8) & 255) + (255 - ((n >> 8) & 255)) * amt));
  const b = Math.min(255, Math.round((n & 255) + (255 - (n & 255)) * amt));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** วาดใบหน้าสไตล์อนิเมะ/Chibi ด้วย DynamicTexture คมชัดสมจริง */
function getChibiFaceMat(
  scene: Scene,
  eyeColor: string,
  hairColor: string,
  fem: boolean,
): StandardMaterial | null {
  if (typeof document === "undefined" && typeof OffscreenCanvas === "undefined") {
    return null;
  }
  let perScene = faceMatCache.get(scene);
  if (!perScene) {
    perScene = new Map();
    faceMatCache.set(scene, perScene);
  }
  const key = `${eyeColor}_${hairColor}_${fem}`;
  const found = perScene.get(key);
  if (found) return found;

  const N = 512;
  const tex = new DynamicTexture(`faceTex-${key}`, { width: N, height: N }, scene, true);
  tex.hasAlpha = true;
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  if (ctx) {
    ctx.clearRect(0, 0, N, N);

    // 1. แก้มอมชมพูฟุ้ง ๆ (Blush) ซ้าย-ขวา
    for (const cx of [120, 392]) {
      const grad = ctx.createRadialGradient(cx, 320, 4, cx, 320, 50);
      grad.addColorStop(0, "rgba(255, 120, 160, 0.75)");
      grad.addColorStop(0.5, "rgba(255, 140, 175, 0.40)");
      grad.addColorStop(1, "rgba(255, 160, 190, 0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, 320, 50, 0, Math.PI * 2);
      ctx.fill();

      // เส้นริ้วแก้มเขินสไตล์มังงะ 3 ขีด
      ctx.strokeStyle = "rgba(255, 75, 125, 0.85)";
      ctx.lineWidth = 3.5;
      ctx.lineCap = "round";
      for (let s = -1; s <= 1; s++) {
        ctx.beginPath();
        ctx.moveTo(cx + s * 14 - 6, 308);
        ctx.lineTo(cx + s * 14 + 6, 328);
        ctx.stroke();
      }
    }

    // 2. ดวงตากลมโตสไตล์อนิเมะ/Chibi
    for (const [cx, flip] of [[155, false], [357, true]] as const) {
      const cy = 240;
      const ew = 58;
      const eh = 78;

      // พื้นตาขาว
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.ellipse(cx, cy, ew, eh, 0, 0, Math.PI * 2);
      ctx.fill();

      // ม่านตาสีตา (Iris)
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(cx, cy + 3, ew - 5, eh - 7, 0, 0, Math.PI * 2);
      ctx.clip();

      const eyeGrad = ctx.createLinearGradient(cx, cy - eh, cx, cy + eh);
      eyeGrad.addColorStop(0, "#0f172a");
      eyeGrad.addColorStop(0.35, eyeColor);
      eyeGrad.addColorStop(0.80, lighten(eyeColor, 0.45));
      eyeGrad.addColorStop(1, lighten(eyeColor, 0.75));
      ctx.fillStyle = eyeGrad;
      ctx.fillRect(cx - ew, cy - eh, ew * 2, eh * 2);

      // รูม่านตาสีดำ (Pupil)
      ctx.fillStyle = "#020617";
      ctx.beginPath();
      ctx.ellipse(cx, cy + 6, ew * 0.45, eh * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();

      // วงประกายแสงในม่านตาล่าง
      ctx.beginPath();
      ctx.arc(cx, cy + eh * 0.45, ew * 0.45, 0.2, Math.PI - 0.2);
      ctx.lineWidth = 7;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.45)";
      ctx.stroke();

      ctx.restore();

      // ขอบตาบนหนาโค้งมน (Eyelash / Eyelid) สไตล์การ์ตูนญี่ปุ่น
      ctx.strokeStyle = "#18181b";
      ctx.lineWidth = fem ? 9.0 : 7.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.ellipse(cx, cy - 3, ew + 3, eh * 0.72, 0, Math.PI * 1.05, Math.PI * 1.95);
      ctx.stroke();

      // หางตา/ขนตาข้างสำหรับหญิง
      if (fem) {
        ctx.lineWidth = 5.0;
        ctx.beginPath();
        const lashX = flip ? cx + ew + 1 : cx - ew - 1;
        const lashEndX = flip ? lashX + 16 : lashX - 16;
        ctx.moveTo(lashX, cy - 14);
        ctx.quadraticCurveTo(lashX, cy - 24, lashEndX, cy - 28);
        ctx.stroke();
      }

      // ประกายแสงตา (Catchlights) วิ้งวับ
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.ellipse(cx - (flip ? -15 : 15), cy - 26, 16, 20, -0.2, 0, Math.PI * 2);
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx + (flip ? -18 : 18), cy + 24, 8, 0, Math.PI * 2);
      ctx.fill();

      // 3. คิ้วด้านบนตา
      ctx.strokeStyle = hairColor;
      ctx.lineWidth = fem ? 6.5 : 8.5;
      ctx.lineCap = "round";
      ctx.beginPath();
      const browY = 115;
      ctx.moveTo(cx - (flip ? 38 : -38), browY + (fem ? 6 : 10));
      ctx.quadraticCurveTo(cx, browY - (fem ? 14 : 8), cx + (flip ? 38 : -38), browY + (fem ? 12 : 2));
      ctx.stroke();
    }

    // 4. รอยยิ้ม Chibi น่ารักตรงกลาง
    ctx.strokeStyle = "#831843";
    ctx.lineWidth = 5.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.arc(256, 355, 20, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();

    // ลิ้นสีชมพูเล็ก ๆ
    ctx.fillStyle = "#fb7185";
    ctx.beginPath();
    ctx.arc(256, 360, 12, 0.1 * Math.PI, 0.9 * Math.PI);
    ctx.fill();

    tex.update();
  }

  const matFace = new StandardMaterial(`mFace-${key}`, scene);
  matFace.diffuseTexture = tex;
  matFace.useAlphaFromDiffuseTexture = true;
  matFace.specularColor = Color3.Black();
  matFace.emissiveColor = new Color3(0.3, 0.3, 0.3);
  matFace.backFaceCulling = true;
  matFace.freeze();
  perScene.set(key, matFace);
  return matFace;
}

/** ทำให้เป็นหน้าเหลี่ยม ได้ลุค low-poly ชัด ๆ */
function flat(mesh: Mesh): Mesh {
  mesh.convertToFlatShadedMesh();
  return mesh;
}

/** เงาจาน ๆ ใต้ตัว ถูกกว่าเงาจริงมากและเข้ากับลุคการ์ตูน */
function blob(scene: Scene, parent: TransformNode, r = 0.6): void {
  const d = MeshBuilder.CreateDisc("sh", { radius: r, tessellation: 10 }, scene);
  d.rotation.x = Math.PI / 2;
  d.position.y = 0.03;
  d.material = mat(scene, "#000000", { alpha: 0.22 });
  d.parent = parent;
  d.isPickable = false;
}

/**
 * จุดที่ของสวมใส่ไปแขวน
 *
 * ผูกกับชิ้นส่วนที่ขยับอยู่แล้ว (แขน ขา ลำตัว) ของที่แขวนจึงแกว่งตามท่าทางเอง
 * โดยที่โค้ดท่าทางไม่ต้องรู้จักของสวมใส่เลย
 */
export type GearAnchors = Record<GearAnchor, TransformNode>;

export interface ChibiModel {
  root: TransformNode;
  /** ลำตัว ใช้ขยับขึ้นลงตอนเดิน */
  body: TransformNode;
  legs: Mesh[];
  arms: Mesh[];
  head: Mesh;
  /** จุดแขวนของสวมใส่ */
  anchors: GearAnchors;
  /**
   * อาวุธและของถือประจำอาชีพ **ซ่อนเมื่อสวมอาวุธจริง**
   * ไม่ลบทิ้งเพราะถอดอาวุธแล้วต้องกลับมาถืออาวุธประจำอาชีพเหมือนเดิม
   */
  classWeapon: TransformNode[];
  /**
   * ของสวมใส่ที่แขวนอยู่ตอนนี้ ล้างชุดนี้ก่อนใส่ชุดใหม่เสมอ
   *
   * **ต้องเก็บเป็นรายการ ไม่ใช่ก้อนแม่ก้อนเดียว** เพราะแต่ละชิ้นต้องไปเกาะ
   * จุดติดตั้งคนละจุดเพื่อให้ขยับตามแขนขา จึงไม่ได้อยู่ใต้ก้อนแม่ร่วมกัน
   * (เคยทำแบบก้อนแม่แล้วล้างไม่โดน ของซ้อนทับเพิ่มขึ้นทุกครั้งที่เปลี่ยนชุด)
   */
  gearPieces: TransformNode[];
  /**
   * ของที่ถือในมือตอนนี้ (อาวุธและโล่) **โชว์เฉพาะตอนต่อสู้**
   * `setGear` คำนวณใหม่ทุกครั้งที่เปลี่ยนของ ส่วนการเปิดปิดเป็นหน้าที่ของ `setHeldVisible`
   */
  held: TransformNode[];
  /**
   * มีเมื่อสลับเป็นโมเดลจริง .glb ที่มีกระดูกแล้ว (`upgradeCharacter`)
   * ท่าทางจะหมุนกระดูกแทนชิ้นแขนขาชิบิ และจุดแขวนในมือย้ายไปตามกระดูกมือทุกเฟรม
   */
  rig?: CharacterRig;
}

/**
 * ทรงผม 12 แบบ (id ใน shared/looks.ts HAIR_STYLES) สร้างจากรูปทรงพื้นฐาน ออกแบบเอง
 * ทุกทรงมี "ฝาผม" ครอบหัวเหมือนกัน แล้วต่อชิ้นเฉพาะทรง · ชิ้นผมทุกชิ้นชื่อขึ้นต้น "hair"
 * ทรงที่ไม่รู้จักได้ฝาผมเปล่า (ไม่พัง) — "wavy" คือผมยาวเดิมของหญิง "spiky" คือผมชี้เดิมของชาย
 */
export function buildHair(scene: Scene, body: TransformNode, style: string, color: string): void {
  const m = mat(scene, color);
  const put = (mesh: Mesh, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): Mesh => {
    mesh.position.set(x, y, z);
    mesh.rotation.set(rx, ry, rz);
    mesh.material = m;
    mesh.parent = body;
    return mesh;
  };
  const sphere = (name: string, d: number, seg = 4): Mesh => flat(MeshBuilder.CreateSphere(name, { diameter: d, segments: seg }, scene));
  const cone = (name: string, top: number, bottom: number, h: number, t = 6): Mesh =>
    flat(MeshBuilder.CreateCylinder(name, { diameterTop: top, diameterBottom: bottom, height: h, tessellation: t }, scene));
  const capD = style === "curly" ? 1.28 : style === "bowl" ? 1.22 : 1.15;
  if (style !== "mohawk") {
    put(flat(MeshBuilder.CreateSphere("hair", { diameter: capD, segments: 4, slice: style === "bowl" ? 0.62 : 0.55 }, scene)), 0, 1.66, -0.05, -0.15);
  } else {
    put(flat(MeshBuilder.CreateSphere("hair", { diameter: 1.09, segments: 4, slice: 0.5 }, scene)), 0, 1.64, -0.04, -0.15);
  }
  switch (style) {
    case "spiky":
      for (let i = 0; i < 4; i++) {
        const a = -0.9 + i * 0.6;
        put(cone("hairsp", 0, 0.34, 0.5, 4), Math.sin(a) * 0.35, 2.12, -0.15 + Math.cos(a) * 0.05, -0.4, 0, -a * 0.7);
      }
      break;
    case "swept":
      put(cone("hairfr", 0.12, 0.42, 0.62, 5), 0.18, 1.98, 0.36, 1.1, 0, -0.9);
      put(cone("hairfr", 0.1, 0.3, 0.45, 5), -0.2, 2.06, 0.3, 1.0, 0, -0.5);
      break;
    case "messy":
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        put(cone("hairms", 0, 0.26, 0.34, 4), Math.cos(a) * 0.38, 2.0 + (i % 2) * 0.08, Math.sin(a) * 0.32 - 0.05, Math.sin(a) * 0.7, 0, -Math.cos(a) * 0.7);
      }
      put(cone("hairms", 0, 0.2, 0.42, 4), 0.05, 2.24, -0.05, -0.3, 0, 0.4);
      break;
    case "bob":
      put(cone("hairbob", 1.2, 1.24, 0.55, 8), 0, 1.4, -0.08);
      put(cone("hairbang", 0.7, 0.8, 0.16, 8), 0, 1.86, 0.3, 0.25);
      break;
    case "twintails":
      for (const x of [-0.55, 0.55]) {
        put(sphere("hairtie", 0.2, 3), x, 1.86, -0.12);
        put(cone("hairtail", 0.32, 0.12, 0.7, 6), x * 1.12, 1.48, -0.18, 0, 0, x > 0 ? 0.35 : -0.35);
      }
      break;
    case "ponytail":
      put(sphere("hairtie", 0.2, 3), 0, 2.0, -0.5);
      put(cone("hairtail", 0.34, 0.1, 0.85, 6), 0, 1.66, -0.72, 0.55);
      put(cone("hairside", 0.22, 0.14, 0.5, 5), 0.44, 1.46, 0.12, 0, 0, -0.12);
      put(cone("hairside", 0.22, 0.14, 0.5, 5), -0.44, 1.46, 0.12, 0, 0, 0.12);
      break;
    case "bun":
      put(sphere("hairbun", 0.5, 5), 0, 2.26, -0.18);
      put(cone("hairside", 0.12, 0.08, 0.4, 5), 0.45, 1.44, 0.12, 0, 0, -0.1);
      put(cone("hairside", 0.12, 0.08, 0.4, 5), -0.45, 1.44, 0.12, 0, 0, 0.1);
      break;
    case "braid":
      for (let i = 0; i < 4; i++) put(sphere("hairbraid", 0.26 - i * 0.03, 3), 0.42 + i * 0.03, 1.4 - i * 0.2, 0.12 + i * 0.05);
      put(cone("hairbang", 0.6, 0.72, 0.14, 8), 0, 1.88, 0.32, 0.3);
      break;
    case "curly":
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        put(sphere("haircurl", 0.34, 3), Math.cos(a) * 0.58, 1.58 + Math.sin(i * 1.7) * 0.18, Math.sin(a) * 0.5 - 0.1);
      }
      for (let i = 0; i < 5; i++) put(sphere("haircurl", 0.36, 3), (i - 2) * 0.22, 2.16 - Math.abs(i - 2) * 0.05, -0.08);
      break;
    case "mohawk":
      for (let i = 0; i < 5; i++) put(cone("hairmo", 0, 0.24, 0.5 - Math.abs(i - 2) * 0.06, 4), 0, 2.16 - Math.abs(i - 2) * 0.05, 0.3 - i * 0.2, -0.25 + i * 0.12);
      break;
    case "bowl":
      put(cone("hairbowl", 1.24, 1.26, 0.14, 10), 0, 1.84, -0.04, -0.1);
      break;
    case "wavy":
    default:
      if (style === "wavy") {
        put(cone("hairback", 0.78, 0.52, 0.95, 7), 0, 1.25, -0.3, 0.12);
        for (const x of [-0.46, 0.46]) put(cone("hairside", 0.3, 0.2, 0.62, 5), x, 1.42, 0.06, 0, 0, x > 0 ? -0.12 : 0.12);
      }
  }
}

/**
 * ตัวละครชิบิ ยืนด้วยเท้าที่ y = 0 ของ root
 *
 * `gender` มีผลกับหน้าตาอย่างเดียว (ทรงผม สัดส่วนลำตัว) **ไม่มีผลกับค่าพลัง**
 * ค่าพลังมาจากอาชีพกับสเตตัสเท่านั้น ตามที่ GDD วางไว้
 */
export function createChibi(
  scene: Scene,
  look: ClassLook,
  skinIn = "#ffd9b8",
  gender: Gender = DEFAULT_GENDER,
  /** หน้าตาที่ผู้เล่นเลือกเอง (shared/looks.ts) ไม่ส่ง = หน้าตาเดิมตามเพศ/อาชีพ */
  custom?: CharacterLook,
): ChibiModel {
  const fem = gender === "female";
  const skin = custom?.skin ?? skinIn;
  const root = new TransformNode("chibi", scene);
  const body = new TransformNode("body", scene);
  body.parent = root;

  // ต่างกันแค่สัดส่วนบาง ๆ พอให้แยกออกในระยะเล่นจริง
  // ไม่ดันให้ต่างกันมากเพราะสไตล์ชิบิหัวโตจะดูแปลกทันที
  // สัดส่วน Chibi Nendoroid โค้งมนนุ่มนวล
  const torso = flat(MeshBuilder.CreateCylinder(
    "torso",
    {
      diameterTop: fem ? 0.56 : 0.66,
      diameterBottom: fem ? 0.86 : 0.80,
      height: 0.75,
      tessellation: 12,
    },
    scene,
  ));
  torso.position.y = 0.78;
  torso.material = mat(scene, look.outfit);
  torso.parent = body;

  // แถบกระดุม/ลวดลายชุดด้านหน้า เพิ่มมิติให้ชุดเสื้อผ้า
  const tunicTrim = flat(MeshBuilder.CreateBox("tunicTrim", { width: 0.12, height: 0.62, depth: 0.06 }, scene));
  tunicTrim.position.set(0, 0.78, fem ? 0.35 : 0.38);
  tunicTrim.material = mat(scene, look.accent);
  tunicTrim.parent = body;

  const belt = MeshBuilder.CreateCylinder("belt", { diameter: 0.78, height: 0.1, tessellation: 12 }, scene);
  belt.position.y = 0.6;
  belt.material = mat(scene, look.accent);
  belt.parent = body;

  // ปกเสื้อ/คอเสื้อ เชื่อมต่อระหว่างศีรษะกับลำตัวให้ดูเป็นชุดเสื้อผ้าสวยงาม
  const collar = flat(MeshBuilder.CreateCylinder("collar", {
    diameterTop: fem ? 0.60 : 0.68,
    diameterBottom: fem ? 0.64 : 0.72,
    height: 0.11,
    tessellation: 12,
  }, scene));
  collar.position.y = 1.15;
  collar.material = mat(scene, look.accent);
  collar.parent = body;

  const head = flat(MeshBuilder.CreateSphere("head", { diameter: 1.06, segments: 7 }, scene));
  head.position.y = 1.6;
  head.material = mat(scene, skin);
  head.parent = body;

  buildHair(scene, body, custom?.hairStyle ?? (fem ? "wavy" : "spiky"), custom?.hair ?? look.hair);

  // ใบหน้าสไตล์อนิเมะ/Chibi ด้วย DynamicTexture คมชัด
  const faceMat = getChibiFaceMat(scene, custom?.eyes ?? "#1d1d2b", custom?.hair ?? look.hair, fem);
  if (faceMat) {
    const facePlate = MeshBuilder.CreatePlane("facePlate", { width: 0.86, height: 0.86 }, scene);
    facePlate.position.set(0, 1.58, 0.505);
    facePlate.material = faceMat;
    facePlate.parent = body;
    facePlate.isPickable = false;
  } else {
    // โหมดทดสอบ (Node.js/NullEngine ไม่มี canvas) ใช้รูปทรง fallback
    for (const x of [-0.18, 0.18]) {
      const e = MeshBuilder.CreateSphere("eye", { diameter: fem ? 0.19 : 0.17, segments: 4 }, scene);
      e.position.set(x, 1.58, 0.47);
      e.scaling.y = 1.35;
      e.material = mat(scene, custom?.eyes ?? "#1d1d2b");
      e.parent = body;

      const h = MeshBuilder.CreateSphere("hl", { diameter: 0.06, segments: 3 }, scene);
      h.position.set(x + 0.03, 1.63, 0.54);
      h.material = mat(scene, "#ffffff", { noLight: true });
      h.parent = body;

      const brow = flat(MeshBuilder.CreateBox("eyebrow", { width: 0.13, height: 0.035, depth: 0.04 }, scene));
      brow.position.set(x, 1.71, 0.48);
      brow.rotation.z = x > 0 ? -0.1 : 0.1;
      brow.material = mat(scene, custom?.hair ?? look.hair);
      brow.parent = body;

      const b = flat(MeshBuilder.CreateSphere("blush", { diameter: 0.13, segments: 3 }, scene));
      b.scaling.set(1.2, 0.55, 0.4);
      b.position.set(x > 0 ? x + 0.10 : x - 0.10, 1.48, 0.46);
      b.material = mat(scene, "#f472b6", { alpha: 0.85, noLight: true });
      b.parent = body;
    }

    const mouth = flat(MeshBuilder.CreateBox("mouth", { width: 0.07, height: 0.025, depth: 0.03 }, scene));
    mouth.position.set(0, 1.41, 0.50);
    mouth.material = mat(scene, "#be185d");
    mouth.parent = body;
  }

  // ใบหูสองข้างศีรษะ
  for (const x of [-0.53, 0.53]) {
    const ear = flat(MeshBuilder.CreateSphere("ear", { diameter: 0.20, segments: 3 }, scene));
    ear.scaling.set(0.35, 0.75, 0.55);
    ear.position.set(x, 1.58, 0.04);
    ear.material = mat(scene, skin);
    ear.parent = body;
  }

  // ขาผูกกับ root ไม่ใช่ body เพื่อให้ตัวเด้งขึ้นลงได้โดยเท้าไม่ลอยตาม
  const legs = [-0.17, 0.17].map((x) => {
    const l = flat(MeshBuilder.CreateBox("leg", { width: 0.24, height: 0.42, depth: 0.28 }, scene));
    l.setPivotPoint(new Vector3(0, 0.2, 0));
    l.position.set(x, 0.22, 0);
    l.material = mat(scene, "#4a3a2c");
    l.parent = root;

    // หัวรองเท้าบูทยื่นไปข้างหน้า เพิ่มความรู้สึกเป็นรองเท้าจริง มั่นคง
    const toe = flat(MeshBuilder.CreateBox("bootToe", { width: 0.22, height: 0.16, depth: 0.16 }, scene));
    toe.position.set(0, -0.13, 0.12);
    toe.material = mat(scene, "#362a20");
    toe.parent = l;

    return l;
  });

  // ท่อนแขนเสื้อสีชุด เชื่อมต่อบริเวณไหล่กับลำตัว
  for (const x of [-0.44, 0.44]) {
    const sleeve = flat(MeshBuilder.CreateCylinder("sleeve", {
      diameterTop: 0.32,
      diameterBottom: 0.28,
      height: 0.18,
      tessellation: 6,
    }, scene));
    sleeve.position.set(x > 0 ? x - 0.03 : x + 0.03, 0.98, 0.05);
    sleeve.rotation.z = x > 0 ? -0.15 : 0.15;
    sleeve.material = mat(scene, look.outfit);
    sleeve.parent = body;
  }

  const arms = [-0.48, 0.48].map((x) => {
    const a = flat(MeshBuilder.CreateSphere("arm", { diameter: 0.28, segments: 3 }, scene));
    a.position.set(x, 0.9, 0.05);
    a.material = mat(scene, skin);
    a.parent = body;
    return a;
  });

  const classWeapon: TransformNode[] = [];
  buildWeapon(scene, look, arms, classWeapon);
  blob(scene, root, 0.55);

  // จุดแขวนของสวมใส่ ผูกกับชิ้นที่ขยับอยู่แล้ว ของจึงแกว่งตามท่าทางเอง
  const anchor = (name: string, parent: TransformNode, y: number, z = 0): TransformNode => {
    const n = new TransformNode(name, scene);
    n.parent = parent;
    n.position.set(0, y, z);
    return n;
  };

  const anchors: GearAnchors = {
    head: anchor("a-head", body, 1.78),
    face: anchor("a-face", body, 1.55, 0.44),
    torso: anchor("a-torso", body, 0.78),
    back: anchor("a-back", body, 1.0, -0.3),
    handL: anchor("a-handL", arms[0]!, 0),
    handR: anchor("a-handR", arms[1]!, 0),
    legs: anchor("a-legs", root, 0.22),
    feet: anchor("a-feet", root, 0.04),
  };

  const model: ChibiModel = {
    root, body, legs, arms, head, anchors, classWeapon,
    gearPieces: [],
    held: [...classWeapon],
  };
  // เริ่มที่เก็บอาวุธ ตัวละครที่ยืนเฉย ๆ ไม่ควรถืออาวุธค้างไว้
  setHeldVisible(model, false);
  return model;
}

/**
 * โชว์หรือเก็บของที่ถือในมือ
 *
 * **กติกา: ถืออาวุธเฉพาะตอนต่อสู้** ยืนอยู่เฉย ๆ หรือเดินทางให้มือเปล่า
 * โล่ก็ถือว่าเป็นของในมือเหมือนกัน จึงเก็บไปพร้อมกัน
 * ถ้าวันหนึ่งอยากให้โล่ติดหลังตลอดเวลา ต้องย้ายมันออกจาก `held`
 */
export function setHeldVisible(model: ChibiModel, on: boolean): void {
  for (const n of model.held) n.setEnabled(on);
}

/**
 * ใส่ของสวมใส่ให้ตัวละคร เรียกซ้ำได้ ของเก่าถูกล้างก่อนเสมอ
 *
 * **ไม่สร้างโมเดลตัวละครใหม่** แค่เปลี่ยนของที่แขวนอยู่ จึงเรียกได้ทุกครั้งที่
 * `equipped` เปลี่ยนโดยไม่กระพริบและไม่เสียท่าทางที่กำลังเล่นอยู่
 */
export function setGear(
  scene: Scene,
  model: ChibiModel,
  looks: EquipLook[],
  weaponRefine = 0,
  wingRefine = 0,
  weaponFlames: readonly string[] = [],
): void {
  // ล้างของชุดเดิมให้หมดก่อน ไม่งั้นใส่ของใหม่แล้วของเก่ายังค้างซ้อนอยู่
  //
  // **ห้ามสั่งลบวัสดุด้วย** (อาร์กิวเมนต์ที่สองของ dispose)
  // วัสดุถูกแคชใช้ร่วมกันทั้งฉากตามสี (ดู `mat`) ลบของตัวเองทีเดียว
  // สีของผู้เล่นคนอื่นที่ใช้สีเดียวกันจะหายไปด้วย กลายเป็นขาวโพลนทั้งตัว
  // โดยไม่มี error ให้เห็นเลย — เคยหลุดไปแล้วตอนทำระบบอุปกรณ์
  for (const piece of model.gearPieces) piece.dispose(false, false);
  model.gearPieces = [];

  // อาวุธที่สวมใส่แทนอาวุธประจำอาชีพ ไม่ใช่ถือสองอัน
  const hasWeapon = looks.some((l) => l.anchor === "handR");
  const held: TransformNode[] = hasWeapon ? [] : [...model.classWeapon];

  for (const look of looks) {
    const isWpn = look.anchor === "handR";
    const isWing = look.shape === "wings";
    const rLvl = isWpn ? weaponRefine : isWing ? wingRefine : 0;
    const piece = buildGearPiece(scene, look, rLvl, isWpn ? weaponFlames : []);
    piece.parent = model.anchors[look.anchor] ?? model.body;
    for (const m of piece.getChildMeshes()) m.isPickable = false;
    // ของในมือเข้ากลุ่มที่ซ่อนตอนไม่ได้ต่อสู้ ของสวมใส่อื่นโชว์ตลอด
    if (look.anchor === "handR" || look.anchor === "handL") held.push(piece);
    else piece.setEnabled(true);
    model.gearPieces.push(piece);
  }

  // อาวุธประจำอาชีพที่ไม่ได้ถูกแทน ยังต้องอยู่ในกลุ่มซ่อน ไม่ใช่เปิดค้างไว้
  for (const n of model.classWeapon) n.setEnabled(false);
  model.held = held;
  setHeldVisible(model, false);
}

/** อาวุธและของถือ ผูกกับแขน จึงแกว่งตามท่าโจมตีเอง */
/**
 * รูปทรงของของสวมใส่หนึ่งชิ้น
 *
 * **ขั้นที่ 1: รูปทรงพื้นฐานต่อกัน** จนกว่าจะมีโมเดลจริงตาม GDD หัวข้อ 11
 * ตอนนั้นฟังก์ชันนี้จะกลายเป็นตัวโหลดไฟล์โมเดลแทน โดยที่ `items.json`
 * กับโค้ดที่เรียกใช้ไม่ต้องเปลี่ยน เพราะคุยกันผ่าน `EquipLook` เหมือนเดิม
 *
 * ตำแหน่งทุกค่าเป็นระยะจากจุดแขวน (ดู `anchors` ใน `createChibi`)
 */
function buildGearPiece(scene: Scene, look: EquipLook, refineLevel = 0, flames: readonly string[] = []): TransformNode {
  const g = new TransformNode(`gear-${look.shape}`, scene);
  const main = look.color;
  const sub = look.accent ?? look.color;
  const put = (m: Mesh): Mesh => {
    m.parent = g;
    return m;
  };

  switch (look.shape) {
    case "cap":
    case "hood": {
      const crown = flat(MeshBuilder.CreateSphere(
        "cap", { diameter: look.shape === "hood" ? 1.3 : 1.2, segments: 5, slice: 0.5 }, scene,
      ));
      crown.position.y = -0.1;
      crown.material = mat(scene, main);
      put(crown);

      // ปีกหมวกด้านหน้า ช่วยให้ดูออกว่าเป็นหมวกไม่ใช่ผมอีกชั้น
      const brim = flat(MeshBuilder.CreateCylinder(
        "brim", { diameter: 1.05, height: 0.07, tessellation: 7 }, scene,
      ));
      brim.position.set(0, -0.12, 0.16);
      brim.scaling.z = 1.25;
      brim.material = mat(scene, sub);
      put(brim);
      break;
    }

    case "mask": {
      const m = flat(MeshBuilder.CreateBox("mask", { width: 0.62, height: 0.3, depth: 0.12 }, scene));
      m.position.y = -0.12;
      m.material = mat(scene, main);
      put(m);
      break;
    }

    case "glasses": {
      for (const x of [-0.18, 0.18]) {
        const l = flat(MeshBuilder.CreateBox("lens", { width: 0.22, height: 0.16, depth: 0.05 }, scene));
        l.position.set(x, 0.03, 0);
        l.material = mat(scene, main);
        put(l);
      }
      break;
    }

    case "vest":
    case "robe": {
      const v = flat(MeshBuilder.CreateCylinder("vest", {
        diameterTop: 0.68,
        diameterBottom: look.shape === "robe" ? 1.0 : 0.9,
        height: look.shape === "robe" ? 0.95 : 0.8,
        tessellation: 7,
      }, scene));
      v.position.y = look.shape === "robe" ? -0.08 : 0;
      v.material = mat(scene, main);
      put(v);

      const trim = MeshBuilder.CreateCylinder("trim", { diameter: 0.84, height: 0.08, tessellation: 7 }, scene);
      trim.position.y = -0.2;
      trim.material = mat(scene, sub);
      put(trim);
      break;
    }

    case "pants": {
      for (const x of [-0.17, 0.17]) {
        const p = flat(MeshBuilder.CreateBox("pant", { width: 0.3, height: 0.3, depth: 0.33 }, scene));
        p.position.set(x, 0.06, 0);
        p.material = mat(scene, main);
        put(p);
      }
      break;
    }

    case "boots": {
      for (const x of [-0.17, 0.17]) {
        const b = flat(MeshBuilder.CreateBox("boot", { width: 0.3, height: 0.22, depth: 0.4 }, scene));
        b.position.set(x, 0.06, 0.04);
        b.material = mat(scene, main);
        put(b);

        const sole = MeshBuilder.CreateBox("sole", { width: 0.32, height: 0.07, depth: 0.42 }, scene);
        sole.position.set(x, -0.04, 0.04);
        sole.material = mat(scene, sub);
        put(sole);
      }
      break;
    }

    case "shield": {
      const sh = flat(MeshBuilder.CreateCylinder("sh", { diameter: 0.95, height: 0.12, tessellation: 7 }, scene));
      sh.rotation.z = Math.PI / 2;
      sh.rotation.y = 0.25;
      sh.position.set(-0.08, 0, 0.12);
      sh.material = mat(scene, main);
      put(sh);

      const boss = MeshBuilder.CreateSphere("boss", { diameter: 0.28, segments: 3 }, scene);
      boss.position.set(-0.17, 0, 0.14);
      boss.material = mat(scene, sub, { em: sub });
      put(boss);
      break;
    }

    case "sword":
    case "greatsword": {
      const big = look.shape === "greatsword";
      const bl = flat(MeshBuilder.CreateBox("bl", {
        width: big ? 0.17 : 0.12, height: big ? 1.25 : 1, depth: 0.05,
      }, scene));
      bl.position.y = big ? 0.7 : 0.55;
      bl.material = mat(scene, main, big ? { em: "#5a4410" } : {});
      put(bl);

      const gd = MeshBuilder.CreateBox("gd", { width: 0.38, height: 0.09, depth: 0.1 }, scene);
      gd.position.y = 0.08;
      gd.material = mat(scene, sub);
      put(gd);
      g.rotation.x = 0.9;
      g.position.set(0.05, 0, 0.1);
      break;
    }

    case "bow": {
      const bow = flat(MeshBuilder.CreateTorus("bow", {
        diameter: 1.2, thickness: 0.08, tessellation: 9,
      }, scene));
      bow.rotation.y = Math.PI / 2;
      bow.position.set(-0.1, 0, 0.1);
      bow.material = mat(scene, main);
      put(bow);

      const str = MeshBuilder.CreateCylinder("str", { diameter: 0.03, height: 1.15, tessellation: 3 }, scene);
      str.position.set(-0.1, 0, 0.42);
      str.material = mat(scene, sub, { noLight: true });
      put(str);
      break;
    }

    case "staff": {
      const st = flat(MeshBuilder.CreateCylinder("st", { diameter: 0.09, height: 1.7, tessellation: 5 }, scene));
      st.position.y = 0.45;
      st.material = mat(scene, main);
      put(st);

      const orb = MeshBuilder.CreateSphere("orb", { diameter: 0.33, segments: 5 }, scene);
      orb.position.y = 1.35;
      orb.material = mat(scene, sub, { em: sub });
      put(orb);
      g.position.set(0.05, 0, 0.1);
      break;
    }

    case "knuckle": {
      const k = flat(MeshBuilder.CreateTorus("knuckle", { diameter: 0.34, thickness: 0.11, tessellation: 7 }, scene));
      k.rotation.x = Math.PI / 2;
      k.position.z = 0.08;
      k.material = mat(scene, main, sub ? { em: sub } : {});
      put(k);

      const spike = MeshBuilder.CreateBox("k-spike", { width: 0.28, height: 0.12, depth: 0.08 }, scene);
      spike.position.set(0, 0, 0.22);
      spike.material = mat(scene, sub ?? main);
      put(spike);
      break;
    }

    case "dagger": {
      const bl = flat(MeshBuilder.CreateBox("bl", { width: 0.09, height: 0.6, depth: 0.04 }, scene));
      bl.position.y = 0.35;
      bl.material = mat(scene, main, sub ? { em: sub } : {});
      put(bl);

      const gd = MeshBuilder.CreateBox("gd", { width: 0.24, height: 0.06, depth: 0.08 }, scene);
      gd.position.y = 0.06;
      gd.material = mat(scene, sub ?? main);
      put(gd);

      g.rotation.x = 0.8;
      g.position.set(0.04, 0, 0.08);
      break;
    }

    case "lantern": {
      const st = flat(MeshBuilder.CreateCylinder("st", { diameter: 0.08, height: 1.6, tessellation: 5 }, scene));
      st.position.y = 0.45;
      st.material = mat(scene, main);
      put(st);

      const lamp = flat(MeshBuilder.CreateBox("lamp", { width: 0.3, height: 0.36, depth: 0.3 }, scene));
      lamp.position.y = 1.25;
      lamp.material = mat(scene, sub ?? "#ffd97d", { em: sub ?? "#ffaa3b" });
      put(lamp);
      g.position.set(0.05, 0, 0.1);
      break;
    }

    case "wings": {
      for (const side of [-1, 1]) {
        const w = flat(MeshBuilder.CreateCylinder("wing", {
          diameterTop: 0.1, diameterBottom: 0.95, height: 1.15, tessellation: 3,
        }, scene));
        w.position.set(side * 0.42, 0.18, -0.05);
        w.rotation.z = side * 0.55;
        w.rotation.x = -0.25;
        w.scaling.z = 0.25;
        w.material = mat(scene, main, { em: sub });
        put(w);
      }
      break;
    }

    default: {
      // ลืมเพิ่มรูปร่างหลังเพิ่มค่าใน GearShape — ให้เห็นเป็นกล่องเทา
      // ดีกว่าหายไปเงียบ ๆ แล้วนึกว่าของไม่ติด
      const box = flat(MeshBuilder.CreateBox("gear?", { size: 0.3 }, scene));
      box.material = mat(scene, "#9aa0ab");
      put(box);
      break;
    }
  }

  if (look.scale && look.scale !== 1) g.scaling.scaleInPlace(look.scale);

  // อาวุธที่ตีบวกสูง (+7 ขึ้นไป) มีออร่าเรืองแสงรอบตัวตาม GDD หัวข้อ 5
  if (look.anchor === "handR" && refineLevel >= 7) {
    const auraColor = refineLevel >= 9 ? "#e056fd" : refineLevel >= 8 ? "#ff7675" : "#ffeaa7";
    const aura = flat(MeshBuilder.CreateCylinder("refine-aura", {
      diameter: 0.35,
      height: 1.2,
      tessellation: 6,
    }, scene));
    aura.position.y = 0.55;
    aura.material = mat(scene, auraColor, { em: auraColor, alpha: 0.35, noLight: true });
    put(aura);
  }

  // ไฟตะเกียง (ประกายวิญญาณ) ที่ใส่ในอาวุธ — ดวงไฟเล็กสีตามธาตุ ลอยเรียงขึ้นตามด้ามอาวุธ ดวงละไฟ
  // วาดจาก `cards` ที่ server sync มา ผู้เล่นคนอื่นจึงเห็นด้วย
  flames.forEach((hex, i) => {
    const f = flat(MeshBuilder.CreateSphere("spark-flame", { diameter: 0.16, segments: 3 }, scene));
    f.position.set(i % 2 === 0 ? 0.1 : -0.1, 0.45 + i * 0.32, 0.05);
    f.scaling.y = 1.5;
    f.material = mat(scene, hex, { em: hex, alpha: 0.85, noLight: true });
    put(f);
  });

  // ปีกที่ตีบวกสูง (+7 ขึ้นไป) มีออร่าปีกเรืองแสง
  if (look.shape === "wings" && refineLevel >= 7) {
    const auraColor = refineLevel >= 9 ? "#e056fd" : refineLevel >= 8 ? "#ff7675" : "#ffeaa7";
    for (const side of [-1, 1]) {
      const aura = flat(MeshBuilder.CreateCylinder("refine-aura-wing", {
        diameterTop: 0.15,
        diameterBottom: 1.1,
        height: 1.25,
        tessellation: 3,
      }, scene));
      aura.position.set(side * 0.42, 0.18, -0.05);
      aura.rotation.z = side * 0.55;
      aura.rotation.x = -0.25;
      aura.scaling.z = 0.35;
      aura.material = mat(scene, auraColor, { em: auraColor, alpha: 0.35, noLight: true });
      put(aura);
    }
  }

  return g;
}

/**
 * อาวุธและของถือประจำอาชีพ ผูกกับแขน จึงแกว่งตามท่าโจมตีเอง
 * ก้อนที่สร้างถูกใส่ลง `out` เพื่อให้ซ่อนทั้งชุดได้ตอนสวมอาวุธจริง
 */
function buildWeapon(
  scene: Scene,
  look: ClassLook,
  arms: Mesh[],
  out: TransformNode[],
): void {
  // **ทุกชิ้นต้องอยู่ใต้ wpn หรือ left เท่านั้น ห้ามผูกกับ arms ตรง ๆ**
  // ไม่งั้นซ่อนอาวุธประจำอาชีพตอนสวมอาวุธจริงไม่ได้
  // (`setEnabled` มีผลกับลูกของ node นั้น ไม่ใช่กับก้อนที่ไปเกาะพี่น้องมัน)
  const left = new TransformNode("lh", scene);
  left.parent = arms[0]!;

  const right = arms[1]!;
  const wpn = new TransformNode("wpn", scene);
  wpn.parent = right;
  wpn.position.set(0.05, 0, 0.1);

  out.push(wpn, left);

  if (look.weapon === "sword") {
    const bl = flat(MeshBuilder.CreateBox("bl", { width: 0.12, height: 1, depth: 0.05 }, scene));
    bl.position.y = 0.55;
    bl.material = mat(scene, "#dfe6ef");
    bl.parent = wpn;

    const gd = MeshBuilder.CreateBox("gd", { width: 0.36, height: 0.08, depth: 0.1 }, scene);
    gd.position.y = 0.08;
    gd.material = mat(scene, "#c99a3b");
    gd.parent = wpn;
    wpn.rotation.x = 0.9;

    const sh = flat(MeshBuilder.CreateCylinder("shield", { diameter: 0.9, height: 0.12, tessellation: 7 }, scene));
    sh.rotation.z = Math.PI / 2;
    sh.rotation.y = 0.25;
    sh.position.set(-0.08, 0, 0.12);
    sh.material = mat(scene, "#a8743f");
    sh.parent = left;

    const boss = MeshBuilder.CreateSphere("boss", { diameter: 0.26, segments: 3 }, scene);
    boss.position.set(-0.16, 0, 0.14);
    boss.material = mat(scene, "#ffb547", { em: "#7a4a10" });
    boss.parent = left;
    return;
  }

  if (look.weapon === "staff" || look.weapon === "lantern") {
    const st = flat(MeshBuilder.CreateCylinder("st", { diameter: 0.09, height: 1.7, tessellation: 5 }, scene));
    st.position.y = 0.45;
    st.material = mat(scene, "#8a5a32");
    st.parent = wpn;

    // ตะเกียงของเมนเดอร์เป็นกล่องแสงห้อยปลายไม้ ไม้เท้าของอาร์คานิสต์เป็นลูกแก้ว
    if (look.weapon === "lantern") {
      const box = flat(MeshBuilder.CreateBox("lamp", { width: 0.28, height: 0.34, depth: 0.28 }, scene));
      box.position.y = 1.22;
      box.material = mat(scene, look.accent, { em: look.accent });
      box.parent = wpn;
    } else {
      const orb = MeshBuilder.CreateSphere("orb", { diameter: 0.32, segments: 5 }, scene);
      orb.position.y = 1.35;
      orb.material = mat(scene, "#c9a5ff", { em: "#8a5cff" });
      orb.parent = wpn;
    }
    return;
  }

  if (look.weapon === "dagger") {
    // กริชคู่ ข้างละเล่ม สั้นและเอียงออกนอกตัว
    for (const [arm, dir] of [[left, -1], [right, 1]] as Array<[Mesh, number]>) {
      const bl = flat(MeshBuilder.CreateBox("dg", { width: 0.09, height: 0.5, depth: 0.04 }, scene));
      bl.position.set(dir * 0.06, 0.2, 0.1);
      bl.rotation.z = dir * 0.5;
      bl.rotation.x = 0.7;
      bl.material = mat(scene, "#cfd8e4");
      bl.parent = arm;
    }
    return;
  }

  if (look.weapon === "fist") {
    // สนับมือ เป็นวงแหวนหนา ๆ ที่ข้อมือทั้งสองข้าง
    for (const arm of [left, right]) {
      const k = MeshBuilder.CreateTorus("knuckle", { diameter: 0.32, thickness: 0.1, tessellation: 7 }, scene);
      k.rotation.x = Math.PI / 2;
      k.position.z = 0.08;
      k.material = mat(scene, look.accent, { em: "#3a2a08" });
      k.parent = arm;
    }
    return;
  }

  // ที่เหลือคือธนู (เรนเจอร์)
  const arc: Vector3[] = [];
  for (let i = 0; i <= 8; i++) {
    const a = -Math.PI / 2 + (i / 8) * Math.PI;
    arc.push(new Vector3(0, Math.sin(a) * 0.6, Math.cos(a) * 0.3));
  }
  const bow = MeshBuilder.CreateTube("bow", { path: arc, radius: 0.04, tessellation: 5 }, scene);
  bow.position.set(-0.05, 0, 0.12);
  bow.material = mat(scene, "#7a4a2b");
  bow.parent = left;

  const str = MeshBuilder.CreateCylinder("str", { diameter: 0.015, height: 1.2, tessellation: 3 }, scene);
  str.position.set(-0.05, 0, 0.12);
  str.material = mat(scene, "#f4e7c8");
  str.parent = left;
}

/**
 * ท่าเดินและท่าตีตามประเภทอาวุธ
 *
 * @param moving      กำลังเคลื่อนที่อยู่ไหม
 * @param t           เวลาสะสม (วินาที) ใช้เป็นเฟสของคลื่น
 * @param swing       ความคืบของท่าตี 0-1 ถ้า 0 คือไม่ได้ตี
 * @param weaponType  ชนิดอาวุธ เช่น sword_1h, greatsword_2h, bow_2h, staff_1h, knuckle_2h, dagger_1h, lantern_1h
 */
/**
 * ขับท่าทางของตัวละครชิบิ (ขา แขน ลำตัว หัว จุดแขวนอาวุธ)
 * @param c           โมเดลที่สร้างจาก chibi()
 * @param moving      กำลังเดินอยู่หรือเปล่า (ถ้าเดิน ขาจะแกว่ง)
 * @param t           เวลาปัจจุบันเป็นวินาที
 * @param swing       ความคืบของท่าตี 0-1 ถ้า 0 คือไม่ได้ตี
 * @param weaponType  ชนิดอาวุธ เช่น sword_1h, greatsword_2h, bow_2h, staff_1h, knuckle_2h, dagger_1h, lantern_1h
 * @param actionType  ประเภทท่าทาง: "attack" (โจมตีปกติ/สกิลกายภาพ), "buff" (ร่ายบัฟ/อธิษฐาน), "spell" (ร่ายเวทมนตร์)
 */
export function animateChibi(
  c: ChibiModel,
  moving: boolean,
  t: number,
  swing = 0,
  weaponType = "sword_1h",
  actionType: "attack" | "buff" | "spell" = "attack",
): void {
  animateChibiParts(c, moving, t, swing, weaponType, actionType);
  if (!c.rig) return;
  // โมเดลจริง: ชิ้นชิบิถูกซ่อนแล้ว แต่ยังขยับต่อเพราะจุดแขวนอาวุธเกาะแขนชิบิอยู่ (ท่าเหวี่ยงอาวุธได้จากตรงนี้)
  // แล้วยกตำแหน่งจุดแขวนในมือไปไว้ที่กระดูกมือจริง
  animateRig(c.rig, moving, t, swing, weaponType, actionType);
  for (const [anchor, bone] of [["handR", "RightHand"], ["handL", "LeftHand"]] as const) {
    const node = c.rig.bones.get(bone)?.node;
    if (!node) continue;
    // ไล่คิดเมทริกซ์จากบนลงล่าง: computeWorldMatrix ของลูกใช้ค่าแคชของพ่อ ซึ่งยังเป็นของเฟรมก่อน
    const chain: TransformNode[] = [];
    for (let n: TransformNode | null = node; n; n = n.parent as TransformNode | null) chain.unshift(n);
    for (const n of chain) n.computeWorldMatrix(true);
    c.anchors[anchor].setAbsolutePosition(node.getAbsolutePosition());
  }
}

function animateChibiParts(
  c: ChibiModel,
  moving: boolean,
  t: number,
  swing: number,
  weaponType: string,
  actionType: "attack" | "buff" | "spell" = "attack",
): void {
  const sw = moving ? Math.sin(t * 12) : 0;
  c.legs[0]!.rotation.x = sw * 0.6;
  c.legs[1]!.rotation.x = -sw * 0.6;

  // ไม่เดินก็ยังหายใจเบา ๆ ไม่ให้ดูเหมือนรูปปั้น
  const breathe = moving ? Math.abs(Math.sin(t * 12)) * 0.08 : Math.sin(t * 2.2) * 0.025;
  c.body.position.y = breathe;

  const baseArmL_z = 0.05 - sw * 0.12;
  const baseArmR_z = 0.05 + sw * 0.12;
  const baseArm_y = 0.9;
  const baseArmL_x = -0.48;
  const baseArmR_x = 0.48;

  if (swing <= 0) {
    c.body.rotation.y = 0;
    c.body.rotation.x = 0;
    c.arms[0]!.position.set(baseArmL_x, baseArm_y, baseArmL_z);
    c.arms[1]!.position.set(baseArmR_x, baseArm_y, baseArmR_z);
    c.arms[0]!.rotation.set(0, 0, 0);
    c.arms[1]!.rotation.set(0, 0, 0);
    return;
  }

  // ส่วนโค้งของท่าตี: พีคตรงกลางจังหวะ (swing = 0.5)
  const arc = Math.sin(swing * Math.PI);

  if (actionType === "buff") {
    // ---- ท่าร่ายบัฟ / อธิษฐาน / ชูพลังศักดิ์สิทธิ์ ----
    c.body.position.y = breathe + arc * 0.14;
    c.body.rotation.x = -arc * 0.15;
    c.body.rotation.y = 0;

    if (weaponType.startsWith("lantern") || weaponType.startsWith("staff")) {
      // เมนเดอร์/เซนต์: ชูสองมือขึ้นฟ้าปลดปล่อยแสงวิญญาณ
      c.arms[1]!.position.set(baseArmR_x - 0.08, baseArm_y + arc * 0.5, baseArmR_z + arc * 0.35);
      c.arms[1]!.rotation.set(arc * 1.5, 0, -arc * 0.3);

      c.arms[0]!.position.set(baseArmL_x + 0.08, baseArm_y + arc * 0.5, baseArmL_z + arc * 0.35);
      c.arms[0]!.rotation.set(arc * 1.5, 0, arc * 0.3);
    } else {
      // สายประชิด: ยกสองมือเบ่งพลังขึ้นระดับอก
      c.arms[1]!.position.set(baseArmR_x - 0.12, baseArm_y + arc * 0.3, baseArmR_z + arc * 0.3);
      c.arms[1]!.rotation.set(arc * 1.0, 0, -arc * 0.4);

      c.arms[0]!.position.set(baseArmL_x + 0.12, baseArm_y + arc * 0.3, baseArmL_z + arc * 0.3);
      c.arms[0]!.rotation.set(arc * 1.0, 0, arc * 0.4);
    }
    return;
  }

  if (actionType === "spell") {
    // ---- ท่าร่ายเวทมนตร์ (Spell Casting & Blast) ----
    c.body.position.y = breathe + arc * 0.1;
    c.body.rotation.y = -arc * 0.22;
    c.body.rotation.x = arc * 0.08;

    // มือขวายื่นคทาชี้เล็ง มือซ้ายผลักคลื่นเวทนำวิถี
    c.arms[1]!.position.set(baseArmR_x - 0.08, baseArm_y + arc * 0.35, baseArmR_z + arc * 0.55);
    c.arms[1]!.rotation.set(arc * 1.2, -arc * 0.2, 0);

    c.arms[0]!.position.set(baseArmL_x + 0.15, baseArm_y + arc * 0.3, baseArmL_z + arc * 0.45);
    c.arms[0]!.rotation.set(arc * 0.9, arc * 0.3, 0);
    return;
  }

  // ---- ท่าโจมตีปกติและสกิลโจมตีตามชนิดอาวุธ ----
  if (weaponType.startsWith("knuckle")) {
    // ---- สนับมือ: ชก 2 มือสลับ 1-2 คอมโบ (หมัดซ้ายแย็บ -> หมัดขวาตรงกระแทก) ----
    if (swing < 0.5) {
      const p1 = Math.sin(swing * 2 * Math.PI);
      // หมัดซ้ายพุ่งไปข้างหน้า
      c.arms[0]!.position.set(baseArmL_x + 0.12, baseArm_y + 0.06, baseArmL_z + Math.max(0, p1) * 0.65);
      c.arms[0]!.rotation.set(Math.max(0, p1) * 0.6, 0, 0);

      // หมัดขวาตั้งการ์ดคุมหน้า
      c.arms[1]!.position.set(baseArmR_x - 0.08, baseArm_y + 0.04, baseArmR_z - 0.08);
      c.arms[1]!.rotation.set(0.3, -0.2, 0);
      c.body.rotation.y = -p1 * 0.3;
    } else {
      const p2 = Math.sin((swing - 0.5) * 2 * Math.PI);
      // หมัดซ้ายดึงกลับมาตั้งการ์ด
      c.arms[0]!.position.set(baseArmL_x + 0.08, baseArm_y + 0.04, baseArmL_z - 0.08);
      c.arms[0]!.rotation.set(0.3, 0.2, 0);

      // หมัดขวาพุ่งตรงกระแทก
      c.arms[1]!.position.set(baseArmR_x - 0.12, baseArm_y + 0.06, baseArmR_z + Math.max(0, p2) * 0.7);
      c.arms[1]!.rotation.set(Math.max(0, p2) * 0.65, 0, 0);
      c.body.rotation.y = p2 * 0.35;
    }
  } else if (weaponType.startsWith("dagger")) {
    // ---- กริช / มีดสั้น: กวัดแกว่งแทงเฉือน 2 มือ (Dual Dagger Flurry / X-Cross Slash) ----
    if (swing < 0.45) {
      // จังหวะ 1: มือขวาแทงทะลวง มือซ้ายง้างเตรียมตวัด
      const d1 = Math.sin((swing / 0.45) * Math.PI);
      c.arms[1]!.position.set(baseArmR_x - 0.12, baseArm_y - 0.04 + d1 * 0.18, baseArmR_z + d1 * 0.7);
      c.arms[1]!.rotation.set(d1 * 0.6, d1 * 0.8, 0);

      c.arms[0]!.position.set(baseArmL_x + 0.06, baseArm_y + 0.08 * d1, baseArmL_z - d1 * 0.15);
      c.arms[0]!.rotation.set(-d1 * 0.4, 0, -d1 * 0.3);
      c.body.rotation.y = -d1 * 0.3;
    } else {
      // จังหวะ 2: มือซ้ายฟันตวัดตัดกากบาท มือขวาดึงกลับมาคุมจังหวะ
      const d2 = Math.sin(((swing - 0.45) / 0.55) * Math.PI);
      c.arms[0]!.position.set(baseArmL_x + 0.14, baseArm_y + d2 * 0.15, baseArmL_z + d2 * 0.65);
      c.arms[0]!.rotation.set(d2 * 0.55, -d2 * 0.7, 0);

      c.arms[1]!.position.set(baseArmR_x - 0.08, baseArm_y, baseArmR_z - 0.1);
      c.arms[1]!.rotation.set(-d2 * 0.2, 0, 0);
      c.body.rotation.y = d2 * 0.32;
    }
  } else if (weaponType.startsWith("bow") || weaponType.startsWith("crossbow")) {
    // ---- ยิงธนู / หน้าไม้: แขนซ้ายเล็งไปข้างหน้า แขนขวาง้างสายแล้วปล่อย ----
    c.arms[0]!.position.set(baseArmL_x + 0.16, baseArm_y + 0.08, 0.38);
    c.arms[0]!.rotation.set(0.2, 0.15, 0);

    const draw = swing < 0.6 ? (swing / 0.6) : (1 - (swing - 0.6) / 0.4);
    c.arms[1]!.position.set(baseArmR_x - 0.1, baseArm_y + 0.05 + draw * 0.2, 0.05 - draw * 0.3 + (1 - draw) * 0.25);
    c.arms[1]!.rotation.set(draw * 0.6, -draw * 0.3, 0);

    c.body.rotation.y = 0.35 * arc;
    c.body.rotation.x = 0;
  } else if (weaponType.startsWith("staff")) {
    // ---- คทา: ชูคทาขึ้นสูง ปลดปล่อยคลื่นเวทมนตร์ ----
    c.body.position.y = breathe + arc * 0.12;
    c.body.rotation.y = -arc * 0.15;
    c.body.rotation.x = -arc * 0.1;

    c.arms[1]!.position.set(baseArmR_x - 0.05, baseArm_y + arc * 0.45, baseArmR_z + arc * 0.35);
    c.arms[1]!.rotation.set(arc * 1.4, 0, -arc * 0.3);

    c.arms[0]!.position.set(baseArmL_x + 0.1, baseArm_y + arc * 0.35, baseArmL_z + arc * 0.25);
    c.arms[0]!.rotation.set(arc * 1.1, 0, arc * 0.3);
  } else if (weaponType.startsWith("lantern")) {
    // ---- ตะเกียงวิญญาณ: ชูตะเกียงเปล่งแสงอธิษฐานศักดิ์สิทธิ์ ----
    c.body.rotation.y = 0;
    c.body.position.y = breathe + arc * 0.08;

    c.arms[1]!.position.set(baseArmR_x - 0.1, baseArm_y + arc * 0.35, baseArmR_z + arc * 0.3);
    c.arms[1]!.rotation.set(arc * 0.8, 0, -arc * 0.25);

    c.arms[0]!.position.set(baseArmL_x + 0.18 * arc, baseArm_y + 0.05 * arc, baseArmL_z + 0.2 * arc);
    c.arms[0]!.rotation.set(arc * 0.6, arc * 0.4, 0);
  } else {
    // ---- ดาบ / ดาบใหญ่: ฟันเฉียงลงด้วยพลัง ----
    const heavy = weaponType.startsWith("greatsword");
    c.body.rotation.y = arc * (heavy ? 0.38 : 0.24);
    c.body.rotation.x = arc * 0.1;

    c.arms[1]!.position.set(baseArmR_x, baseArm_y + arc * (heavy ? 0.4 : 0.28), baseArmR_z + arc * (heavy ? 0.6 : 0.5));
    c.arms[1]!.rotation.set(arc * 1.3, -arc * 0.4, -arc * 0.3);

    if (heavy) {
      c.arms[0]!.position.set(baseArmL_x + 0.2, baseArm_y + arc * 0.25, baseArmL_z + arc * 0.45);
      c.arms[0]!.rotation.set(arc * 1.0, -arc * 0.3, 0);
    } else {
      c.arms[0]!.position.set(baseArmL_x, baseArm_y, baseArmL_z - arc * 0.25);
      c.arms[0]!.rotation.set(-arc * 0.4, 0, 0);
    }
  }
}

export interface MonsterModel {
  root: TransformNode;
  body: TransformNode;
  /** ปีก (ผึ้งและราชินี) ใช้ขยับเร็ว ๆ ให้ดูบิน */
  wings: Mesh[];
  /** ความสูงของจุดกลางตัว ใช้วางแถบเลือดและเอฟเฟกต์ */
  centerY: number;
  /** มีเมื่อสลับเป็น .glb ที่ใส่กระดูกแล้ว (มอนสองขา) — เดิน/ตีด้วยกระดูกแทนการเด้งทั้งตัว */
  rig?: CharacterRig;
}

function eyes(scene: Scene, parent: TransformNode, y: number, z: number, s = 1): void {
  for (const x of [-0.16 * s, 0.16 * s]) {
    const e = MeshBuilder.CreateSphere("e", { diameter: 0.14 * s, segments: 3 }, scene);
    e.position.set(x, y, z);
    e.scaling.y = 1.3;
    e.material = mat(scene, "#1d1d2b");
    e.parent = parent;
  }
}

/** โมเดลมอนสเตอร์ตามชนิดใน monsters.json ยืนด้วยเท้าที่ y = 0 ของ root */
export function createMonsterModel(scene: Scene, type: string): MonsterModel {
  const root = new TransformNode(type, scene);
  const body = new TransformNode("b", scene);
  body.parent = root;
  const wings: Mesh[] = [];
  let shadowR = 0.6;
  let centerY = 0.6;

  if (type === "slime") {
    const s = flat(MeshBuilder.CreateSphere("s", { diameter: 1.15, segments: 5 }, scene));
    s.scaling.y = 0.78;
    s.position.y = 0.45;
    s.material = mat(scene, "#6fd36a", { alpha: 0.93 });
    s.parent = body;
    eyes(scene, body, 0.55, 0.45);

    const st = MeshBuilder.CreateCylinder("stem", { diameter: 0.06, height: 0.3, tessellation: 4 }, scene);
    st.position.y = 0.98;
    st.material = mat(scene, "#3c7a2a");
    st.parent = body;

    for (const d of [-1, 1]) {
      const l = flat(MeshBuilder.CreateSphere("leaf", { diameter: 0.32, segments: 3 }, scene));
      l.scaling.set(1, 0.25, 0.55);
      l.position.set(d * 0.15, 1.12, 0);
      l.rotation.z = d * 0.5;
      l.material = mat(scene, "#4fbf45");
      l.parent = body;
    }
    centerY = 0.5;
  } else if (type === "bee" || type === "queen") {
    const k = type === "queen" ? 2.1 : 1;
    // ผึ้งลอยอยู่ ตัว body จึงยกขึ้นจากพื้น
    body.position.y = 1.1 * k;

    const s = flat(MeshBuilder.CreateSphere("s", { diameter: 0.9 * k, segments: 5 }, scene));
    s.scaling.set(0.95, 0.9, 1.2);
    s.material = mat(scene, "#ffd23f");
    s.parent = body;

    for (const z of [-0.12, 0.16]) {
      const r = MeshBuilder.CreateTorus("r", { diameter: 0.86 * k, thickness: 0.1 * k, tessellation: 10 }, scene);
      r.rotation.x = Math.PI / 2;
      r.position.z = z * k;
      r.scaling.set(1, 1, 0.9);
      r.material = mat(scene, "#2b2320");
      r.parent = body;
    }
    eyes(scene, body, 0.12 * k, 0.5 * k, k);

    const sting = MeshBuilder.CreateCylinder(
      "sg",
      { diameterTop: 0, diameterBottom: 0.16 * k, height: 0.32 * k, tessellation: 4 },
      scene,
    );
    sting.rotation.x = -Math.PI / 2;
    sting.position.z = -0.6 * k;
    sting.material = mat(scene, "#2b2320");
    sting.parent = body;

    for (const d of [-1, 1]) {
      const w = MeshBuilder.CreateDisc("w", { radius: 0.38 * k, tessellation: 8 }, scene);
      w.position.set(d * 0.3 * k, 0.38 * k, -0.05);
      w.rotation.x = Math.PI / 2;
      w.scaling.x = 0.6;
      w.material = mat(scene, "#ffffff", { alpha: 0.55, noLight: true });
      w.parent = body;
      wings.push(w);
    }

    if (type === "queen") {
      const cr = flat(MeshBuilder.CreateCylinder(
        "cr",
        { diameterTop: 0.75, diameterBottom: 0.55, height: 0.35, tessellation: 6 },
        scene,
      ));
      cr.position.set(0, 0.95, 0.15);
      cr.material = mat(scene, "#ffcf5a", { em: "#a06a10" });
      cr.parent = body;
    }
    shadowR = 0.45 * k;
    centerY = 1.1 * k;
  } else if (type === "boar") {
    const s = flat(MeshBuilder.CreateSphere("s", { diameter: 1.3, segments: 4 }, scene));
    s.scaling.set(0.9, 0.8, 1.3);
    s.position.y = 0.72;
    s.material = mat(scene, "#8a5a3a");
    s.parent = body;

    const sn = flat(MeshBuilder.CreateCylinder("sn", { diameter: 0.42, height: 0.3, tessellation: 6 }, scene));
    sn.rotation.x = Math.PI / 2;
    sn.position.set(0, 0.64, 0.9);
    sn.material = mat(scene, "#e9a3a0");
    sn.parent = body;

    for (const d of [-1, 1]) {
      const tk = MeshBuilder.CreateCylinder(
        "tk",
        { diameterTop: 0, diameterBottom: 0.1, height: 0.3, tessellation: 4 },
        scene,
      );
      tk.position.set(d * 0.2, 0.72, 0.92);
      tk.rotation.z = d * -0.5;
      tk.material = mat(scene, "#fff6e0");
      tk.parent = body;

      const ear = flat(MeshBuilder.CreateCylinder(
        "ear",
        { diameterTop: 0, diameterBottom: 0.26, height: 0.3, tessellation: 4 },
        scene,
      ));
      ear.position.set(d * 0.3, 1.18, 0.45);
      ear.material = mat(scene, "#6e4429");
      ear.parent = body;
    }
    eyes(scene, body, 0.95, 0.7);

    for (const [x, z] of [[-0.3, 0.4], [0.3, 0.4], [-0.3, -0.4], [0.3, -0.4]]) {
      const l = flat(MeshBuilder.CreateBox("lg", { width: 0.2, height: 0.36, depth: 0.2 }, scene));
      l.position.set(x!, 0.18, z!);
      l.material = mat(scene, "#5a3a26");
      l.parent = root;
    }
    shadowR = 0.8;
    centerY = 0.8;
  } else if (type === "rabbit") {
    const s = flat(MeshBuilder.CreateSphere("s", { diameter: 0.9, segments: 5 }, scene));
    s.scaling.set(0.85, 0.8, 1.05);
    s.position.y = 0.4;
    s.material = mat(scene, "#f3ead8");
    s.parent = body;

    const tail = flat(MeshBuilder.CreateSphere("tl", { diameter: 0.26, segments: 3 }, scene));
    tail.position.set(0, 0.45, -0.5);
    tail.material = mat(scene, "#ffffff");
    tail.parent = body;

    for (const d of [-1, 1]) {
      const ear = flat(MeshBuilder.CreateSphere("ear", { diameter: 0.5, segments: 3 }, scene));
      ear.scaling.set(0.3, 1, 0.2);
      ear.position.set(d * 0.16, 0.95, 0.05);
      ear.rotation.z = d * -0.2;
      ear.material = mat(scene, "#e8dcc4");
      ear.parent = body;
    }

    // เขาเดียวกลางหน้าผาก จุดที่ทำให้มันไม่ใช่กระต่ายธรรมดา
    const horn = MeshBuilder.CreateCylinder(
      "hn",
      { diameterTop: 0, diameterBottom: 0.12, height: 0.32, tessellation: 4 },
      scene,
    );
    horn.position.set(0, 0.82, 0.32);
    horn.rotation.x = 0.5;
    horn.material = mat(scene, "#c9a46a");
    horn.parent = body;

    eyes(scene, body, 0.5, 0.42);
    shadowR = 0.5;
    centerY = 0.45;
  } else if (type === "mushroom") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    const stem = flat(MeshBuilder.CreateCylinder("stem", { diameterTop: 0.38 * k, diameterBottom: 0.48 * k, height: 0.6 * k, tessellation: 6 }, scene));
    stem.position.y = 0.3 * k;
    stem.material = mat(scene, "#f5eedc");
    stem.parent = body;

    const cap = flat(MeshBuilder.CreateSphere("cap", { diameter: 1.15 * k, segments: 5 }, scene));
    cap.scaling.set(1.1, 0.55, 1.1);
    cap.position.y = 0.62 * k;
    cap.material = mat(scene, "#e63946");
    cap.parent = body;

    for (const [x, z] of [[-0.25, 0.2], [0.28, 0.15], [-0.15, -0.3], [0.2, -0.25]] as [number, number][]) {
      const spot = flat(MeshBuilder.CreateSphere("spot", { diameter: 0.18 * k, segments: 3 }, scene));
      spot.position.set(x * k, 0.72 * k, z * k);
      spot.material = mat(scene, "#ffffff");
      spot.parent = body;
    }
    eyes(scene, body, 0.38 * k, 0.22 * k, k * 0.7);
    shadowR = 0.55 * k;
    centerY = 0.55 * k;
  } else if (type === "wolf" || type === "wolf_alpha") {
    const isAlpha = type === "wolf_alpha";
    const k = (MONSTERS[type]?.look?.size ?? 1) * (isAlpha ? 1.3 : 1);
    const col = isAlpha ? "#3a3f4b" : "#5c6370";

    const torso = flat(MeshBuilder.CreateBox("torso", { width: 0.48 * k, height: 0.5 * k, depth: 0.95 * k }, scene));
    torso.position.y = 0.5 * k;
    torso.material = mat(scene, col);
    torso.parent = body;

    const head = flat(MeshBuilder.CreateBox("head", { width: 0.42 * k, height: 0.42 * k, depth: 0.45 * k }, scene));
    head.position.set(0, 0.72 * k, 0.55 * k);
    head.material = mat(scene, col);
    head.parent = body;

    const snout = flat(MeshBuilder.CreateBox("snout", { width: 0.24 * k, height: 0.2 * k, depth: 0.32 * k }, scene));
    snout.position.set(0, 0.62 * k, 0.88 * k);
    snout.material = mat(scene, isAlpha ? "#2d323d" : "#4a505c");
    snout.parent = body;

    const nose = flat(MeshBuilder.CreateBox("nose", { width: 0.1 * k, height: 0.08 * k, depth: 0.08 * k }, scene));
    nose.position.set(0, 0.7 * k, 1.04 * k);
    nose.material = mat(scene, "#111111");
    nose.parent = body;

    for (const d of [-1, 1]) {
      const ear = flat(MeshBuilder.CreateCylinder("ear", { diameterTop: 0, diameterBottom: 0.18 * k, height: 0.32 * k, tessellation: 3 }, scene));
      ear.position.set(d * 0.16 * k, 1.02 * k, 0.5 * k);
      ear.rotation.z = d * -0.25;
      ear.material = mat(scene, col);
      ear.parent = body;
    }

    const tail = flat(MeshBuilder.CreateCylinder("tail", { diameterTop: 0.06 * k, diameterBottom: 0.16 * k, height: 0.5 * k, tessellation: 4 }, scene));
    tail.position.set(0, 0.65 * k, -0.6 * k);
    tail.rotation.x = -0.7;
    tail.material = mat(scene, col);
    tail.parent = body;

    for (const [lx, lz] of [[-0.2, 0.35], [0.2, 0.35], [-0.2, -0.35], [0.2, -0.35]] as [number, number][]) {
      const leg = flat(MeshBuilder.CreateBox("leg", { width: 0.16 * k, height: 0.42 * k, depth: 0.16 * k }, scene));
      leg.position.set(lx * k, 0.21 * k, lz * k);
      leg.material = mat(scene, isAlpha ? "#2d323d" : "#4a505c");
      leg.parent = body;
    }
    eyes(scene, body, 0.76 * k, 0.76 * k, k * 0.7);
    shadowR = 0.65 * k;
    centerY = 0.6 * k;
  } else if (type === "rock_crab" || type === "iron_crab") {
    const isIron = type === "iron_crab";
    const k = MONSTERS[type]?.look?.size ?? 1;
    const shellCol = isIron ? "#4a5568" : "#8a7d6e";
    const clawCol = isIron ? "#718096" : "#a39281";

    const shell = flat(MeshBuilder.CreateSphere("shell", { diameter: 1.1 * k, segments: 4 }, scene));
    shell.scaling.set(1.2, 0.45, 1.0);
    shell.position.y = 0.35 * k;
    shell.material = mat(scene, shellCol);
    shell.parent = body;

    for (const d of [-1, 1]) {
      const eyeStalk = MeshBuilder.CreateCylinder("stalk", { diameter: 0.08 * k, height: 0.25 * k, tessellation: 4 }, scene);
      eyeStalk.position.set(d * 0.25 * k, 0.52 * k, 0.45 * k);
      eyeStalk.material = mat(scene, shellCol);
      eyeStalk.parent = body;

      const eye = flat(MeshBuilder.CreateSphere("crabEye", { diameter: 0.14 * k, segments: 3 }, scene));
      eye.position.set(d * 0.25 * k, 0.64 * k, 0.45 * k);
      eye.material = mat(scene, "#111111");
      eye.parent = body;

      const clawArm = flat(MeshBuilder.CreateCylinder("clawArm", { diameter: 0.14 * k, height: 0.4 * k, tessellation: 4 }, scene));
      clawArm.position.set(d * 0.55 * k, 0.35 * k, 0.4 * k);
      clawArm.rotation.z = d * -0.6;
      clawArm.material = mat(scene, clawCol);
      clawArm.parent = body;

      const pincer = flat(MeshBuilder.CreateBox("pincer", { width: 0.28 * k, height: 0.18 * k, depth: 0.45 * k }, scene));
      pincer.position.set(d * 0.75 * k, 0.4 * k, 0.65 * k);
      pincer.material = mat(scene, clawCol);
      pincer.parent = body;

      for (const legZ of [-0.2, 0, 0.2]) {
        const leg = flat(MeshBuilder.CreateCylinder("leg", { diameter: 0.08 * k, height: 0.4 * k, tessellation: 4 }, scene));
        leg.position.set(d * 0.65 * k, 0.18 * k, legZ * k);
        leg.rotation.z = d * -0.7;
        leg.material = mat(scene, shellCol);
        leg.parent = body;
      }
    }
    shadowR = 0.7 * k;
    centerY = 0.4 * k;
  } else if (type === "gull") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    body.position.y = 0.9 * k;

    const b = flat(MeshBuilder.CreateSphere("gullBody", { diameter: 0.75 * k, segments: 4 }, scene));
    b.scaling.set(0.8, 0.75, 1.2);
    b.position.y = 0.3 * k;
    b.material = mat(scene, "#f7fafc");
    b.parent = body;

    const beak = flat(MeshBuilder.CreateCylinder("beak", { diameterTop: 0, diameterBottom: 0.14 * k, height: 0.35 * k, tessellation: 3 }, scene));
    beak.position.set(0, 0.32 * k, 0.65 * k);
    beak.rotation.x = Math.PI / 2;
    beak.material = mat(scene, "#ecc94b");
    beak.parent = body;

    for (const d of [-1, 1]) {
      const wing = flat(MeshBuilder.CreateBox("gullWing", { width: 0.08 * k, height: 0.32 * k, depth: 0.65 * k }, scene));
      wing.position.set(d * 0.35 * k, 0.38 * k, -0.05 * k);
      wing.rotation.z = d * -0.2;
      wing.material = mat(scene, "#718096");
      wing.parent = body;
    }
    eyes(scene, body, 0.42 * k, 0.42 * k, k * 0.6);
    shadowR = 0.45 * k;
    centerY = 1.0 * k;
  } else if (type === "bat") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    body.position.y = 1.1 * k;

    const b = flat(MeshBuilder.CreateSphere("batBody", { diameter: 0.65 * k, segments: 4 }, scene));
    b.position.y = 0.3 * k;
    b.material = mat(scene, "#2d3748");
    b.parent = body;

    for (const d of [-1, 1]) {
      const ear = flat(MeshBuilder.CreateCylinder("batEar", { diameterTop: 0, diameterBottom: 0.22 * k, height: 0.35 * k, tessellation: 3 }, scene));
      ear.position.set(d * 0.2 * k, 0.65 * k, 0.05 * k);
      ear.rotation.z = d * -0.2;
      ear.material = mat(scene, "#4a5568");
      ear.parent = body;

      const wing = flat(MeshBuilder.CreateBox("batWing", { width: 0.55 * k, height: 0.04 * k, depth: 0.4 * k }, scene));
      wing.position.set(d * 0.48 * k, 0.35 * k, 0);
      wing.rotation.y = d * 0.3;
      wing.material = mat(scene, "#1a202c");
      wing.parent = body;
    }
    eyes(scene, body, 0.35 * k, 0.28 * k, k * 0.6);
    shadowR = 0.45 * k;
    centerY = 1.1 * k;
  } else if (type === "root_doll" || type === "root_guardian" || type === "haunted_tree") {
    const isGiant = type === "root_guardian";
    const isHaunted = type === "haunted_tree";
    const k = MONSTERS[type]?.look?.size ?? 1;

    const trunk = flat(MeshBuilder.CreateCylinder("trunk", {
      diameterTop: 0.55 * k,
      diameterBottom: 0.75 * k,
      height: 1.2 * k,
      tessellation: 6,
    }, scene));
    trunk.position.y = 0.6 * k;
    trunk.material = mat(scene, isHaunted ? "#38281d" : "#6c4d32");
    trunk.parent = body;

    const foliage = flat(MeshBuilder.CreateSphere("foliage", { diameter: 1.2 * k, segments: 5 }, scene));
    foliage.scaling.set(1.1, 0.7, 1.1);
    foliage.position.y = 1.35 * k;
    foliage.material = mat(scene, isHaunted ? "#4a3b32" : (isGiant ? "#2e7d32" : "#43a047"));
    foliage.parent = body;

    for (const d of [-1, 1]) {
      const arm = flat(MeshBuilder.CreateCylinder("arm", { diameter: 0.16 * k, height: 0.65 * k, tessellation: 4 }, scene));
      arm.position.set(d * 0.45 * k, 0.75 * k, 0);
      arm.rotation.z = d * -0.6;
      arm.material = mat(scene, isHaunted ? "#38281d" : "#6c4d32");
      arm.parent = body;
    }
    if (isHaunted) {
      for (const x of [-0.15 * k, 0.15 * k]) {
        const eye = flat(MeshBuilder.CreateSphere("evilEye", { diameter: 0.16 * k, segments: 3 }, scene));
        eye.position.set(x, 0.75 * k, 0.32 * k);
        eye.material = mat(scene, "#ff4444", { em: "#ff2222", noLight: true });
        eye.parent = body;
      }
    } else {
      eyes(scene, body, 0.75 * k, 0.32 * k, k * 0.7);
    }
    shadowR = 0.65 * k;
    centerY = 0.7 * k;
  } else if (type === "scorpion") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    const bodySeg = flat(MeshBuilder.CreateSphere("scorpBody", { diameter: 0.85 * k, segments: 4 }, scene));
    bodySeg.scaling.set(0.8, 0.5, 1.2);
    bodySeg.position.y = 0.32 * k;
    bodySeg.material = mat(scene, "#b7791f");
    bodySeg.parent = body;

    for (const d of [-1, 1]) {
      const arm = flat(MeshBuilder.CreateCylinder("scorpArm", { diameter: 0.12 * k, height: 0.45 * k, tessellation: 4 }, scene));
      arm.position.set(d * 0.4 * k, 0.35 * k, 0.45 * k);
      arm.rotation.z = d * -0.5;
      arm.material = mat(scene, "#975a16");
      arm.parent = body;

      const pincer = flat(MeshBuilder.CreateBox("scorpPincer", { width: 0.18 * k, height: 0.12 * k, depth: 0.35 * k }, scene));
      pincer.position.set(d * 0.55 * k, 0.38 * k, 0.7 * k);
      pincer.material = mat(scene, "#744210");
      pincer.parent = body;
    }

    const tail1 = flat(MeshBuilder.CreateCylinder("tail1", { diameter: 0.16 * k, height: 0.5 * k, tessellation: 4 }, scene));
    tail1.position.set(0, 0.55 * k, -0.6 * k);
    tail1.rotation.x = -0.8;
    tail1.material = mat(scene, "#975a16");
    tail1.parent = body;

    const tail2 = flat(MeshBuilder.CreateCylinder("tail2", { diameter: 0.14 * k, height: 0.5 * k, tessellation: 4 }, scene));
    tail2.position.set(0, 0.85 * k, -0.45 * k);
    tail2.rotation.x = 0.8;
    tail2.material = mat(scene, "#975a16");
    tail2.parent = body;

    const stinger = flat(MeshBuilder.CreateCylinder("stinger", { diameterTop: 0, diameterBottom: 0.14 * k, height: 0.28 * k, tessellation: 4 }, scene));
    stinger.position.set(0, 0.95 * k, -0.2 * k);
    stinger.rotation.x = 1.6;
    stinger.material = mat(scene, "#c53030");
    stinger.parent = body;

    eyes(scene, body, 0.42 * k, 0.55 * k, k * 0.6);
    shadowR = 0.65 * k;
    centerY = 0.4 * k;
  } else if (type === "mummy") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    const bodyBox = flat(MeshBuilder.CreateBox("mummyBody", { width: 0.5 * k, height: 0.9 * k, depth: 0.4 * k }, scene));
    bodyBox.position.y = 0.55 * k;
    bodyBox.material = mat(scene, "#d6c8b0");
    bodyBox.parent = body;

    const headBox = flat(MeshBuilder.CreateBox("mummyHead", { width: 0.46 * k, height: 0.46 * k, depth: 0.42 * k }, scene));
    headBox.position.y = 1.15 * k;
    headBox.material = mat(scene, "#c4b59d");
    headBox.parent = body;

    for (const d of [-1, 1]) {
      const arm = flat(MeshBuilder.CreateBox("mummyArm", { width: 0.16 * k, height: 0.16 * k, depth: 0.55 * k }, scene));
      arm.position.set(d * 0.32 * k, 0.85 * k, 0.28 * k);
      arm.material = mat(scene, "#c4b59d");
      arm.parent = body;

      const eye = flat(MeshBuilder.CreateSphere("mummyEye", { diameter: 0.12 * k, segments: 3 }, scene));
      eye.position.set(d * 0.14 * k, 1.16 * k, 0.22 * k);
      eye.material = mat(scene, "#ff2222", { em: "#ff0000", noLight: true });
      eye.parent = body;
    }
    shadowR = 0.45 * k;
    centerY = 0.7 * k;
  } else if (type === "bone_knight" || type === "headless_knight") {
    const isHeadless = type === "headless_knight";
    const k = MONSTERS[type]?.look?.size ?? 1;
    const armorCol = isHeadless ? "#2d3748" : "#a0aec0";

    const torso = flat(MeshBuilder.CreateBox("knightTorso", { width: 0.55 * k, height: 0.65 * k, depth: 0.45 * k }, scene));
    torso.position.y = 0.65 * k;
    torso.material = mat(scene, armorCol);
    torso.parent = body;

    if (!isHeadless) {
      const helm = flat(MeshBuilder.CreateBox("helm", { width: 0.42 * k, height: 0.45 * k, depth: 0.42 * k }, scene));
      helm.position.y = 1.15 * k;
      helm.material = mat(scene, "#cbd5e0");
      helm.parent = body;

      for (const d of [-1, 1]) {
        const horn = flat(MeshBuilder.CreateCylinder("horn", { diameterTop: 0, diameterBottom: 0.12 * k, height: 0.35 * k, tessellation: 3 }, scene));
        horn.position.set(d * 0.24 * k, 1.4 * k, 0);
        horn.rotation.z = d * -0.4;
        horn.material = mat(scene, "#e2e8f0");
        horn.parent = body;
      }
      eyes(scene, body, 1.15 * k, 0.22 * k, k * 0.6);
    } else {
      const pumpkin = flat(MeshBuilder.CreateSphere("spectralHead", { diameter: 0.45 * k, segments: 4 }, scene));
      pumpkin.position.set(-0.35 * k, 0.65 * k, 0.25 * k);
      pumpkin.material = mat(scene, "#4299e1", { em: "#3182ce", noLight: true });
      pumpkin.parent = body;
    }

    const sword = flat(MeshBuilder.CreateBox("sword", { width: 0.1 * k, height: 0.9 * k, depth: 0.15 * k }, scene));
    sword.position.set(0.45 * k, 0.65 * k, 0.15 * k);
    sword.material = mat(scene, "#e2e8f0");
    sword.parent = body;

    const shield = flat(MeshBuilder.CreateBox("shield", { width: 0.12 * k, height: 0.55 * k, depth: 0.45 * k }, scene));
    shield.position.set(-0.42 * k, 0.65 * k, 0.1 * k);
    shield.material = mat(scene, isHeadless ? "#1a202c" : "#718096");
    shield.parent = body;

    shadowR = 0.55 * k;
    centerY = 0.7 * k;
  } else if (type === "candle_wisp") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    body.position.y = 0.5 * k;

    const wax = flat(MeshBuilder.CreateCylinder("wax", { diameter: 0.5 * k, height: 0.8 * k, tessellation: 6 }, scene));
    wax.position.y = 0.4 * k;
    wax.material = mat(scene, "#f7fafc");
    wax.parent = body;

    const flame = flat(MeshBuilder.CreateSphere("candleFlame", { diameter: 0.35 * k, segments: 4 }, scene));
    flame.scaling.set(0.8, 1.4, 0.8);
    flame.position.y = 0.95 * k;
    flame.material = mat(scene, "#dd6b20", { em: "#f6ad55", noLight: true });
    flame.parent = body;

    eyes(scene, body, 0.45 * k, 0.26 * k, k * 0.6);
    shadowR = 0.4 * k;
    centerY = 0.65 * k;
  } else if (type === "poison_frog") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    const fBody = flat(MeshBuilder.CreateSphere("frogBody", { diameter: 0.95 * k, segments: 4 }, scene));
    fBody.scaling.set(1.2, 0.75, 1.0);
    fBody.position.y = 0.38 * k;
    fBody.material = mat(scene, "#38a169");
    fBody.parent = body;

    for (const d of [-1, 1]) {
      const bulb = flat(MeshBuilder.CreateSphere("bulbEye", { diameter: 0.3 * k, segments: 3 }, scene));
      bulb.position.set(d * 0.35 * k, 0.68 * k, 0.25 * k);
      bulb.material = mat(scene, "#48bb78");
      bulb.parent = body;

      const pupil = flat(MeshBuilder.CreateSphere("frogPupil", { diameter: 0.14 * k, segments: 3 }, scene));
      pupil.position.set(d * 0.35 * k, 0.72 * k, 0.36 * k);
      pupil.material = mat(scene, "#111111");
      pupil.parent = body;
    }
    shadowR = 0.6 * k;
    centerY = 0.4 * k;
  } else if (type === "swamp_witch") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    const robe = flat(MeshBuilder.CreateCylinder("robe", { diameterTop: 0.35 * k, diameterBottom: 0.8 * k, height: 1.1 * k, tessellation: 6 }, scene));
    robe.position.y = 0.55 * k;
    robe.material = mat(scene, "#44337a");
    robe.parent = body;

    const head = flat(MeshBuilder.CreateSphere("witchHead", { diameter: 0.45 * k, segments: 4 }, scene));
    head.position.y = 1.15 * k;
    head.material = mat(scene, "#d6bcfa");
    head.parent = body;

    const hatBrim = flat(MeshBuilder.CreateCylinder("hatBrim", { diameter: 0.95 * k, height: 0.06 * k, tessellation: 8 }, scene));
    hatBrim.position.y = 1.35 * k;
    hatBrim.material = mat(scene, "#2d1b4e");
    hatBrim.parent = body;

    const hatCone = flat(MeshBuilder.CreateCylinder("hatCone", { diameterTop: 0, diameterBottom: 0.55 * k, height: 0.75 * k, tessellation: 5 }, scene));
    hatCone.position.set(0, 1.7 * k, -0.05 * k);
    hatCone.rotation.x = -0.2;
    hatCone.material = mat(scene, "#2d1b4e");
    hatCone.parent = body;

    const staff = flat(MeshBuilder.CreateCylinder("witchStaff", { diameter: 0.08 * k, height: 1.3 * k, tessellation: 4 }, scene));
    staff.position.set(0.48 * k, 0.65 * k, 0.15 * k);
    staff.material = mat(scene, "#6c4d32");
    staff.parent = body;

    const orb = flat(MeshBuilder.CreateSphere("staffOrb", { diameter: 0.24 * k, segments: 4 }, scene));
    orb.position.set(0.48 * k, 1.3 * k, 0.15 * k);
    orb.material = mat(scene, "#9f7aea", { em: "#805ad5", noLight: true });
    orb.parent = body;

    eyes(scene, body, 1.15 * k, 0.2 * k, k * 0.6);
    shadowR = 0.55 * k;
    centerY = 0.7 * k;
  } else if (type === "crystal_golem" || type === "lava_golem") {
    const isLava = type === "lava_golem";
    const k = MONSTERS[type]?.look?.size ?? 1;
    const bodyCol = isLava ? "#442618" : "#4a5568";
    const coreCol = isLava ? "#ed8936" : "#4fd1c5";

    const torso = flat(MeshBuilder.CreateBox("golemTorso", { width: 0.9 * k, height: 0.95 * k, depth: 0.65 * k }, scene));
    torso.position.y = 0.75 * k;
    torso.material = mat(scene, bodyCol);
    torso.parent = body;

    const core = flat(MeshBuilder.CreateBox("golemCore", { width: 0.35 * k, height: 0.35 * k, depth: 0.2 * k }, scene));
    core.position.set(0, 0.78 * k, 0.3 * k);
    core.material = mat(scene, coreCol, { em: coreCol, noLight: true });
    core.parent = body;

    for (const d of [-1, 1]) {
      const pauldron = flat(MeshBuilder.CreateBox("pauldron", { width: 0.45 * k, height: 0.45 * k, depth: 0.45 * k }, scene));
      pauldron.position.set(d * 0.68 * k, 1.1 * k, 0);
      pauldron.rotation.z = d * -0.4;
      pauldron.material = mat(scene, isLava ? "#c05621" : "#319795");
      pauldron.parent = body;

      const fist = flat(MeshBuilder.CreateBox("fist", { width: 0.35 * k, height: 0.45 * k, depth: 0.35 * k }, scene));
      fist.position.set(d * 0.72 * k, 0.45 * k, 0.15 * k);
      fist.material = mat(scene, bodyCol);
      fist.parent = body;
    }
    shadowR = 0.75 * k;
    centerY = 0.75 * k;
  } else if (type === "sandworm" || type === "glass_snake") {
    const isSnake = type === "glass_snake";
    const k = MONSTERS[type]?.look?.size ?? 1;
    const col = isSnake ? "#38a169" : "#c09355";

    for (let i = 0; i < 4; i++) {
      const seg = flat(MeshBuilder.CreateSphere(`seg${i}`, { diameter: (0.75 - i * 0.08) * k, segments: 4 }, scene));
      seg.position.set(0, (0.35 + (3 - i) * 0.25) * k, (i * 0.32) * k);
      seg.material = mat(scene, col);
      seg.parent = body;
    }
    const head = flat(MeshBuilder.CreateSphere("wormHead", { diameter: 0.7 * k, segments: 4 }, scene));
    head.position.set(0, 1.25 * k, 0);
    head.material = mat(scene, isSnake ? "#276749" : "#9c6f38");
    head.parent = body;

    eyes(scene, body, 1.35 * k, 0.25 * k, k * 0.6);
    shadowR = 0.65 * k;
    centerY = 0.8 * k;
  } else if (type === "crystal_drake" || type === "fire_drake") {
    const isFire = type === "fire_drake";
    const k = MONSTERS[type]?.look?.size ?? 1;
    const drakeCol = isFire ? "#d9381e" : "#805ad5";
    body.position.y = 0.8 * k;

    const dTorso = flat(MeshBuilder.CreateSphere("dTorso", { diameter: 1.0 * k, segments: 4 }, scene));
    dTorso.scaling.set(0.8, 0.7, 1.3);
    dTorso.position.y = 0.45 * k;
    dTorso.material = mat(scene, drakeCol);
    dTorso.parent = body;

    const dHead = flat(MeshBuilder.CreateBox("dHead", { width: 0.42 * k, height: 0.38 * k, depth: 0.55 * k }, scene));
    dHead.position.set(0, 0.75 * k, 0.65 * k);
    dHead.material = mat(scene, drakeCol);
    dHead.parent = body;

    for (const d of [-1, 1]) {
      const horn = flat(MeshBuilder.CreateCylinder("dHorn", { diameterTop: 0, diameterBottom: 0.12 * k, height: 0.4 * k, tessellation: 3 }, scene));
      horn.position.set(d * 0.18 * k, 1.0 * k, 0.5 * k);
      horn.rotation.x = -0.6;
      horn.rotation.z = d * -0.3;
      horn.material = mat(scene, isFire ? "#f6ad55" : "#d6bcfa");
      horn.parent = body;

      const wing = flat(MeshBuilder.CreateBox("dWing", { width: 0.7 * k, height: 0.04 * k, depth: 0.5 * k }, scene));
      wing.position.set(d * 0.65 * k, 0.6 * k, 0.1 * k);
      wing.rotation.y = d * 0.4;
      wing.rotation.z = d * -0.2;
      wing.material = mat(scene, isFire ? "#c53030" : "#6b46c1");
      wing.parent = body;
    }
    eyes(scene, body, 0.78 * k, 0.85 * k, k * 0.6);
    shadowR = 0.7 * k;
    centerY = 0.9 * k;
  } else if (type === "yeti" || type === "ice_bear") {
    const isBear = type === "ice_bear";
    const k = MONSTERS[type]?.look?.size ?? 1;

    const torso = flat(MeshBuilder.CreateSphere("yetiTorso", { diameter: 1.3 * k, segments: 4 }, scene));
    torso.scaling.set(0.95, 1.1, 1.1);
    torso.position.y = 0.75 * k;
    torso.material = mat(scene, "#edf2f7");
    torso.parent = body;

    const head = flat(MeshBuilder.CreateSphere("yetiHead", { diameter: 0.75 * k, segments: 4 }, scene));
    head.position.set(0, 1.45 * k, 0.2 * k);
    head.material = mat(scene, "#e2e8f0");
    head.parent = body;

    for (const d of [-1, 1]) {
      const arm = flat(MeshBuilder.CreateCylinder("yetiArm", { diameter: 0.28 * k, height: 0.85 * k, tessellation: 4 }, scene));
      arm.position.set(d * 0.65 * k, 0.75 * k, 0.1 * k);
      arm.rotation.z = d * -0.3;
      arm.material = mat(scene, "#cbd5e0");
      arm.parent = body;
    }
    eyes(scene, body, 1.5 * k, 0.52 * k, k * 0.7);
    shadowR = 0.85 * k;
    centerY = 0.85 * k;
  } else if (type === "frost_giant" || type === "magma_lord" || type === "abyss_overlord") {
    const isMagma = type === "magma_lord";
    const isAbyss = type === "abyss_overlord";
    const k = MONSTERS[type]?.look?.size ?? 1;
    const col = isMagma ? "#990000" : (isAbyss ? "#1a0933" : "#7aa6d6");

    const torso = flat(MeshBuilder.CreateBox("bossTorso", { width: 1.1 * k, height: 1.2 * k, depth: 0.8 * k }, scene));
    torso.position.y = 1.0 * k;
    torso.material = mat(scene, col);
    torso.parent = body;

    const head = flat(MeshBuilder.CreateBox("bossHead", { width: 0.65 * k, height: 0.65 * k, depth: 0.6 * k }, scene));
    head.position.set(0, 1.85 * k, 0.1 * k);
    head.material = mat(scene, col);
    head.parent = body;

    for (const d of [-1, 1]) {
      const horn = flat(MeshBuilder.CreateCylinder("bossHorn", { diameterTop: 0, diameterBottom: 0.2 * k, height: 0.6 * k, tessellation: 4 }, scene));
      horn.position.set(d * 0.38 * k, 2.3 * k, 0.1 * k);
      horn.rotation.z = d * -0.4;
      horn.material = mat(scene, isMagma ? "#f6ad55" : (isAbyss ? "#9f7aea" : "#bee3f8"));
      horn.parent = body;

      const arm = flat(MeshBuilder.CreateBox("bossArm", { width: 0.45 * k, height: 1.1 * k, depth: 0.45 * k }, scene));
      arm.position.set(d * 0.85 * k, 0.85 * k, 0.1 * k);
      arm.material = mat(scene, col);
      arm.parent = body;
    }
    eyes(scene, body, 1.85 * k, 0.42 * k, k * 0.8);
    shadowR = 1.1 * k;
    centerY = 1.1 * k;
  } else if (type === "void_specter" || type === "abyss_stalker") {
    const k = MONSTERS[type]?.look?.size ?? 1;
    body.position.y = 0.8 * k;

    const cowl = flat(MeshBuilder.CreateCylinder("cowl", { diameterTop: 0.35 * k, diameterBottom: 0.75 * k, height: 1.0 * k, tessellation: 5 }, scene));
    cowl.position.y = 0.5 * k;
    cowl.material = mat(scene, "#2c1b4d");
    cowl.parent = body;

    const vHead = flat(MeshBuilder.CreateSphere("vHead", { diameter: 0.5 * k, segments: 4 }, scene));
    vHead.position.set(0, 1.05 * k, 0.05 * k);
    vHead.material = mat(scene, "#1a0933");
    vHead.parent = body;

    for (const x of [-0.14 * k, 0.14 * k]) {
      const eye = flat(MeshBuilder.CreateSphere("voidEye", { diameter: 0.14 * k, segments: 3 }, scene));
      eye.position.set(x, 1.08 * k, 0.26 * k);
      eye.material = mat(scene, "#9f7aea", { em: "#805ad5", noLight: true });
      eye.parent = body;
    }
    shadowR = 0.5 * k;
    centerY = 0.8 * k;
  } else {
    // ชนิดที่ยังไม่มีโมเดลเฉพาะ: รูปร่างตามเผ่า สีจาก look วงแสงธาตุใต้ตัว (race-model.ts)
    const def = MONSTERS[type];
    const look = def?.look;
    const k = look?.size ?? 1;
    const isDummy = !!def?.dummy;
    const res = buildRaceModel(
      scene, root, body, def?.race, def?.element, look?.color ?? "#9b8bb4", k, !!look?.float, !!def?.boss,
      (hex, opts) => mat(scene, hex, opts), flat, isDummy,
    );
    shadowR = res.shadowR;
    centerY = res.centerY;
  }

  blob(scene, root, shadowR);
  return { root, body, wings, centerY };
}

/** ท่าของมอนสเตอร์ สไลม์เด้ง ผึ้งกระพือปีก หมูป่าโยกตัว และท่าพุ่งโจมตี */
export function animateMonster(m: MonsterModel, type: string, moving: boolean, t: number, swing = 0): void {
  const arc = swing > 0 ? Math.sin(swing * Math.PI) : 0;

  if (MONSTERS[type]?.dummy) {
    // หุ่นฟางซ้อม: ไม่เดินไปไหน เมื่อถูกโจมตีจะโยกตัวไปข้างหลังแล้วเด้งกลับแบบสปริง
    m.body.position.y = 0;
    m.body.rotation.x = arc > 0 ? -arc * 0.35 : Math.sin(t * 1.2) * 0.02;
    m.body.position.z = arc > 0 ? -arc * 0.18 : 0;
    return;
  }

  if (m.rig) {
    // จังหวะต่างกันนิดหน่อยต่อชนิด ฝูงเดียวกันจะได้ไม่ก้าวพร้อมกันเป๊ะเหมือนทหารเดินแถว
    animateRig(m.rig, moving, t * 0.8 + type.length, swing, "sword_1h");
    m.body.position.z = arc * 0.25;
    return;
  }

  if (type === "slime") {
    const hop = moving ? Math.abs(Math.sin(t * 7)) : Math.abs(Math.sin(t * 2)) * 0.3;
    m.body.position.y = hop * 0.22;
    m.body.position.z = arc * 0.5; // พุ่งโหม่งไปข้างหน้า
    m.body.scaling.y = (1 - hop * 0.12) - arc * 0.2;
    m.body.scaling.z = 1 + arc * 0.3;
    return;
  }

  const look = MONSTERS[type]?.look;
  if (look) {
    // ตัวที่ยังไม่มีโมเดลจริง: ลอยก็โยกขึ้นลง เดินก็เด้งเบา ๆ ให้รู้ว่ามีชีวิต
    const base = look.float ? 1.1 : 0;
    const bob = look.float ? Math.sin(t * 3) * 0.15 : moving ? Math.abs(Math.sin(t * 8)) * 0.15 : 0;
    m.body.position.y = base + bob;
    m.body.position.z = arc * 0.45;
    m.body.rotation.x = arc * 0.25;
    return;
  }

  if (type === "rabbit") {
    // กระโดดสูงกว่าสไลม์แต่ไม่ยุบตัว ยืนนิ่งแค่ขยับหูเบา ๆ
    const hop = moving ? Math.abs(Math.sin(t * 9)) * 0.35 : 0;
    m.body.position.y = hop + arc * 0.25;
    m.body.position.z = arc * 0.45;
    m.body.rotation.x = moving ? -Math.sin(t * 9) * 0.12 : (arc > 0 ? arc * 0.3 : Math.sin(t * 1.5) * 0.03);
    return;
  }

  if (type === "bee" || type === "queen") {
    const k = type === "queen" ? 2.1 : 1;
    m.body.position.y = 1.1 * k + Math.sin(t * 3.5) * 0.12 - arc * 0.25;
    m.body.position.z = arc * 0.65;
    m.body.rotation.x = arc * 0.45;
    // ปีกกระพือเร็วขึ้นเมื่อกำลังโจมตี
    const flutterSpeed = swing > 0 ? 60 : 40;
    for (const w of m.wings) w.scaling.x = 0.6 * (0.4 + Math.abs(Math.sin(t * flutterSpeed)) * 0.8);
    return;
  }

  if (type === "boar") {
    m.body.rotation.z = moving ? Math.sin(t * 9) * 0.08 : 0;
    m.body.position.y = moving ? Math.abs(Math.sin(t * 9)) * 0.05 : 0;
    m.body.position.z = arc * 0.6;
    m.body.rotation.x = -arc * 0.3; // ก้มหัวขวิดด้วยเขี้ยว
  }
}

/* ---------- สัตว์เลี้ยง ---------- */

/**
 * ตัวที่เดินตามผู้เล่น **คนละชนิดกับของสวมใส่**
 *
 * ของสวมใส่แขวนกับจุดบนตัวละครและขยับตามท่าทางเอง ส่วนสัตว์เลี้ยงเป็นก้อนอิสระ
 * ที่อยู่ระดับฉาก มีตำแหน่งของตัวเองในโลก (ดู `client/src/pet.ts`)
 * **ห้ามเอาไปผูกเป็นลูกของ mesh ผู้เล่น** ไม่งั้นมันจะติดหนึบไปกับตัวเจ้าของ
 * หมุนตามทุกองศา แล้วการเดินตามหลังแบบมีหน่วงจะหมดความหมาย
 */
export interface PetModel {
  /** ก้อนนอกสุด ตำแหน่งในโลกตั้งที่นี่ เท้าอยู่ที่ y = 0 */
  root: TransformNode;
  /** ลำตัว ใช้ขยับขึ้นลงตอนเดินหรือลอย */
  body: TransformNode;
  legs: Mesh[];
  /** ปีก (ตัวที่บิน) */
  wings: Mesh[];
  /** ลอยกลางอากาศไหม ตัวที่ลอยไม่ขยับขา */
  float: boolean;
}

/**
 * โมเดลสัตว์เลี้ยงตาม `PetLook` ใน items.json
 *
 * **ขั้นที่ 1: รูปทรงพื้นฐานต่อกัน** เหมือนของสวมใส่ จะเปลี่ยนเป็นโหลดไฟล์โมเดลจริง
 * ตาม GDD หัวข้อ 11 พร้อมกัน โดย `items.json` ไม่ต้องแก้ เพราะคุยกันผ่าน `PetLook`
 */
export function createPetModel(scene: Scene, look: PetLook, level = 0): PetModel {
  const isEvolved = level >= 10;
  const root = new TransformNode(`pet-${look.shape}`, scene);
  const body = new TransformNode("pb", scene);
  body.parent = root;
  const legs: Mesh[] = [];
  const wings: Mesh[] = [];
  const main = isEvolved ? (look.accent ?? look.color) : look.color;
  const sub = isEvolved ? "#ffd700" : (look.accent ?? look.color);

  switch (look.shape) {
    case "wisp": {
      // ก้อนแสงเรือง ๆ ไส้ในสว่างกับเปลือกโปร่งซ้อนกัน
      const core = MeshBuilder.CreateSphere("core", { diameter: isEvolved ? 0.44 : 0.34, segments: 6 }, scene);
      core.material = mat(scene, isEvolved ? "#fff3a8" : main, { em: isEvolved ? "#ffcc00" : main, noLight: true });
      core.parent = body;

      const halo = MeshBuilder.CreateSphere("halo", { diameter: isEvolved ? 0.72 : 0.56, segments: 6 }, scene);
      halo.material = mat(scene, isEvolved ? "#ffd24a" : sub, { em: isEvolved ? "#ffa500" : sub, alpha: 0.35, noLight: true });
      halo.parent = body;

      // เศษแสงรอบตัว
      const count = isEvolved ? 5 : 3;
      for (let i = 0; i < count; i++) {
        const sp = MeshBuilder.CreateSphere("sp", { diameter: isEvolved ? 0.12 : 0.1, segments: 3 }, scene);
        const a = (i / count) * Math.PI * 2;
        sp.position.set(Math.cos(a) * (isEvolved ? 0.42 : 0.32), Math.sin(a) * 0.18, Math.sin(a) * (isEvolved ? 0.42 : 0.32));
        sp.material = mat(scene, isEvolved ? "#ffe600" : sub, { em: isEvolved ? "#ffaa00" : sub, noLight: true });
        sp.parent = body;
      }
      break;
    }

    case "cub": {
      const torso = flat(MeshBuilder.CreateSphere("t", { diameter: 0.6, segments: 5 }, scene));
      torso.scaling.set(1, 0.85, 1.35);
      torso.position.y = 0.3;
      torso.material = mat(scene, main);
      torso.parent = body;

      const head = flat(MeshBuilder.CreateSphere("h", { diameter: 0.42, segments: 5 }, scene));
      head.position.set(0, 0.42, 0.38);
      head.material = mat(scene, main);
      head.parent = body;

      const snout = flat(MeshBuilder.CreateCylinder(
        "sn", { diameter: 0.17, height: 0.14, tessellation: 5 }, scene,
      ));
      snout.rotation.x = Math.PI / 2;
      snout.position.set(0, 0.37, 0.58);
      snout.material = mat(scene, sub);
      snout.parent = body;

      eyes(scene, body, 0.48, 0.55, 0.7);

      // เขี้ยวคู่สีทองเมื่อร่างวิวัฒนาการ
      if (isEvolved) {
        for (const side of [-1, 1]) {
          const tusk = flat(MeshBuilder.CreateCylinder("tusk", { diameterTop: 0, diameterBottom: 0.08, height: 0.22, tessellation: 4 }, scene));
          tusk.position.set(side * 0.11, 0.42, 0.62);
          tusk.rotation.x = -0.5;
          tusk.rotation.z = side * 0.3;
          tusk.material = mat(scene, "#ffd700", { em: "#ffa000", noLight: true });
          tusk.parent = body;
        }
        // เกราะทองคาดหลัง
        const armor = flat(MeshBuilder.CreateBox("arm", { width: 0.5, height: 0.12, depth: 0.4 }, scene));
        armor.position.set(0, 0.58, 0.3);
        armor.material = mat(scene, "#ffd700", { em: "#d4af37" });
        armor.parent = body;
      }

      for (const [sx, sz] of [[-1, 1], [1, 1], [-1, -1], [1, -1]] as const) {
        const l = MeshBuilder.CreateCylinder("lg", { diameter: 0.12, height: 0.3, tessellation: 4 }, scene);
        const pivot = new TransformNode("lp", scene);
        pivot.position.set(sx * 0.17, 0.3, sz * 0.26);
        pivot.parent = body;
        l.position.y = -0.15;
        l.material = mat(scene, sub);
        l.parent = pivot;
        legs.push(l);
      }
      break;
    }

    case "beetle": {
      const shell = flat(MeshBuilder.CreateSphere("sh", { diameter: 0.48, segments: 5 }, scene));
      shell.scaling.set(1, 0.7, 1.2);
      shell.material = mat(scene, main);
      shell.parent = body;

      const belly = flat(MeshBuilder.CreateSphere("bl", { diameter: 0.4, segments: 4 }, scene));
      belly.scaling.set(1, 0.5, 1.1);
      belly.position.y = -0.1;
      belly.material = mat(scene, sub);
      belly.parent = body;

      eyes(scene, body, 0.06, 0.28, 0.55);

      // เขาด้วงสีทองเมื่อวิวัฒนาการ
      if (isEvolved) {
        const horn = flat(MeshBuilder.CreateCylinder("horn", { diameterTop: 0.03, diameterBottom: 0.1, height: 0.35, tessellation: 5 }, scene));
        horn.position.set(0, 0.26, 0.45);
        horn.rotation.x = 0.6;
        horn.material = mat(scene, "#ffd700", { em: "#ffa500", noLight: true });
        horn.parent = body;
      }

      for (const side of [-1, 1]) {
        const w = MeshBuilder.CreateDisc("w", { radius: isEvolved ? 0.35 : 0.26, tessellation: 6 }, scene);
        w.rotation.x = Math.PI / 2;
        w.position.set(side * 0.2, 0.12, -0.05);
        w.scaling.z = 0.7;
        w.material = mat(scene, isEvolved ? "#ffe680" : "#ffffff", { alpha: 0.45, em: isEvolved ? "#ffaa00" : undefined, noLight: true });
        w.parent = body;
        wings.push(w);
      }
      break;
    }

    default: {
      const box = flat(MeshBuilder.CreateBox("pet?", { size: 0.35 }, scene));
      box.position.y = 0.25;
      box.material = mat(scene, "#9aa0ab");
      box.parent = body;
      break;
    }
  }

  // วงแหวนออร่ามงกุฎแสงเมื่อวิวัฒนาการเต็มวัย (Lv 10)
  if (isEvolved) {
    const crown = flat(MeshBuilder.CreateTorus("evoCrown", { diameter: 0.42, thickness: 0.04, tessellation: 16 }, scene));
    crown.position.y = look.shape === "wisp" ? 0.55 : 0.72;
    crown.material = mat(scene, "#ffe066", { em: "#ffb500", noLight: true });
    crown.parent = body;
  }

  const float = look.float === true;
  if (float) body.position.y = 0.95;
  blob(scene, root, (float ? 0.3 : 0.38) * (isEvolved ? 1.3 : 1));

  for (const m of root.getChildMeshes()) m.isPickable = false;
  const baseScale = look.scale ?? 1;
  const finalScale = baseScale * (isEvolved ? 1.35 : 1);
  if (finalScale !== 1) root.scaling.scaleInPlace(finalScale);
  return { root, body, legs, wings, float };
}

/**
 * ท่าทางของสัตว์เลี้ยง
 *
 * @param moving กำลังวิ่งตามเจ้าของอยู่ไหม
 * @param t      เวลาสะสม (วินาที) ใช้เป็นเฟสของคลื่น
 */
export function animatePet(p: PetModel, moving: boolean, t: number): void {
  if (p.float) {
    // ลอยขึ้นลงตลอดเวลา ไม่งั้นตอนยืนนิ่งจะดูเหมือนก้อนค้างกลางอากาศ
    p.body.position.y = 0.95 + Math.sin(t * 2.6) * 0.07;
    p.body.rotation.y = t * (moving ? 2.4 : 0.8);
    for (const w of p.wings) w.scaling.x = 0.4 + Math.abs(Math.sin(t * 34)) * 0.8;
    return;
  }

  const sw = moving ? Math.sin(t * 13) : 0;
  for (let i = 0; i < p.legs.length; i++) {
    const pivot = p.legs[i]!.parent as TransformNode | null;
    // ขาหน้ากับขาหลังสลับเฟสกัน (คู่ที่ 0,3 กับ 1,2) ได้จังหวะเดินสี่ขา
    if (pivot) pivot.rotation.x = sw * 0.5 * (i === 0 || i === 3 ? 1 : -1);
  }
  p.body.position.y = moving ? Math.abs(Math.sin(t * 13)) * 0.05 : Math.sin(t * 2.2) * 0.015;
}
